(() => {
  // vendor/van-1.5.5.js
  var protoOf = Object.getPrototypeOf;
  var changedStates;
  var derivedStates;
  var curDeps;
  var curNewDerives;
  var alwaysConnectedDom = { isConnected: 1 };
  var gcCycleInMs = 1e3;
  var statesToGc;
  var propSetterCache = {};
  var objProto = protoOf(alwaysConnectedDom);
  var funcProto = protoOf(protoOf);
  var _undefined;
  var addAndScheduleOnFirst = (set, s, f, waitMs) => (
    // patched to use microtasks for less latency when waitMs is not set
    (set ?? (waitMs ? setTimeout(f, waitMs) : Promise.resolve().then(f), /* @__PURE__ */ new Set())).add(s)
  );
  var runAndCaptureDeps = (f, deps, arg) => {
    let prevDeps = curDeps;
    curDeps = deps;
    try {
      return f(arg);
    } catch (e) {
      console.error(e);
      return arg;
    } finally {
      curDeps = prevDeps;
    }
  };
  var keepConnected = (l) => l.filter((b) => b._dom?.isConnected);
  var addStatesToGc = (d) => statesToGc = addAndScheduleOnFirst(statesToGc, d, () => {
    for (let s of statesToGc)
      s._bindings = keepConnected(s._bindings), s._listeners = keepConnected(s._listeners);
    statesToGc = _undefined;
  }, gcCycleInMs);
  var stateProto = {
    get val() {
      curDeps?._getters?.add(this);
      return this.rawVal;
    },
    get oldVal() {
      curDeps?._getters?.add(this);
      return this._oldVal;
    },
    set val(v) {
      curDeps?._setters?.add(this);
      if (v !== this.rawVal) {
        this.rawVal = v;
        this._bindings.length + this._listeners.length ? (derivedStates?.add(this), changedStates = addAndScheduleOnFirst(changedStates, this, updateDoms)) : this._oldVal = v;
      }
    }
  };
  var state = (initVal) => ({
    __proto__: stateProto,
    rawVal: initVal,
    _oldVal: initVal,
    _bindings: [],
    _listeners: []
  });
  var bind = (f, dom2) => {
    let deps = { _getters: /* @__PURE__ */ new Set(), _setters: /* @__PURE__ */ new Set() }, binding = { f }, prevNewDerives = curNewDerives;
    curNewDerives = [];
    let newDom = runAndCaptureDeps(f, deps, dom2);
    newDom = (newDom ?? document).nodeType ? newDom : new Text(newDom);
    for (let d of deps._getters)
      deps._setters.has(d) || (addStatesToGc(d), d._bindings.push(binding));
    for (let l of curNewDerives) l._dom = newDom;
    curNewDerives = prevNewDerives;
    return binding._dom = newDom;
  };
  var derive = (f, s = state(), dom2) => {
    let deps = { _getters: /* @__PURE__ */ new Set(), _setters: /* @__PURE__ */ new Set() }, listener = { f, s };
    listener._dom = dom2 ?? curNewDerives?.push(listener) ?? alwaysConnectedDom;
    s.val = runAndCaptureDeps(f, deps, s.rawVal);
    for (let d of deps._getters)
      deps._setters.has(d) || (addStatesToGc(d), d._listeners.push(listener));
    return s;
  };
  var add = (dom2, ...children) => {
    for (let c of children.flat(Infinity)) {
      let protoOfC = protoOf(c ?? 0);
      let child = protoOfC === stateProto ? bind(() => c.val) : protoOfC === funcProto ? bind(c) : c;
      child != _undefined && dom2.append(child);
    }
    return dom2;
  };
  var tag = (ns, name, ...args) => {
    let [{ is, ...props }, ...children] = protoOf(args[0] ?? 0) === objProto ? args : [{}, ...args];
    let dom2 = ns ? document.createElementNS(ns, name, { is }) : document.createElement(name, { is });
    for (let [k, v] of Object.entries(props)) {
      let getPropDescriptor = (proto) => proto ? Object.getOwnPropertyDescriptor(proto, k) ?? getPropDescriptor(protoOf(proto)) : _undefined;
      let cacheKey = name + "," + k;
      let propSetter = propSetterCache[cacheKey] ??= getPropDescriptor(protoOf(dom2))?.set ?? 0;
      let setter = k.startsWith("on") ? (v2, oldV) => {
        let event = k.slice(2);
        dom2.removeEventListener(event, oldV);
        dom2.addEventListener(event, v2);
      } : propSetter ? propSetter.bind(dom2) : dom2.setAttribute.bind(dom2, k);
      let protoOfV = protoOf(v ?? 0);
      k.startsWith("on") || protoOfV === funcProto && (v = derive(v), protoOfV = stateProto);
      protoOfV === stateProto ? bind(() => (setter(v.val, v._oldVal), dom2)) : setter(v);
    }
    return add(dom2, children);
  };
  var handler = (ns) => ({ get: (_, name) => tag.bind(_undefined, ns, name) });
  var update = (dom2, newDom) => newDom ? newDom !== dom2 && dom2.replaceWith(newDom) : dom2.remove();
  var updateDoms = () => {
    let iter = 0, derivedStatesArray = [...changedStates].filter((s) => s.rawVal !== s._oldVal);
    do {
      derivedStates = /* @__PURE__ */ new Set();
      for (let l of new Set(derivedStatesArray.flatMap((s) => s._listeners = keepConnected(s._listeners))))
        derive(l.f, l.s, l._dom), l._dom = _undefined;
    } while (++iter < 100 && (derivedStatesArray = [...derivedStates]).length);
    let changedStatesArray = [...changedStates].filter((s) => s.rawVal !== s._oldVal);
    changedStates = _undefined;
    for (let b of new Set(changedStatesArray.flatMap((s) => s._bindings = keepConnected(s._bindings))))
      update(b._dom, bind(b.f, b._dom)), b._dom = _undefined;
    for (let s of changedStatesArray) s._oldVal = s.rawVal;
  };
  var van_1_5_5_default = {
    tags: new Proxy((ns) => new Proxy(tag, handler(ns)), handler()),
    hydrate: (dom2, f) => update(dom2, bind(f, dom2)),
    add,
    state,
    derive
  };

  // toolbox-v2.js
  var config = function() {
    const isDebug = document.cookie.indexOf("debugTimestamp=1") !== -1;
    const isLocalhostOrIp = Boolean(location.hostname.match(/^(localhost|\[.+\]|[^a-zA-Z]+)$/i));
    const cdnPath = isLocalhostOrIp ? "" : "https://cdn.myshoptet.com/usr/" + location.host;
    return {
      cdnPath,
      snippetPath: cdnPath + "/user/documents/upload/snippets/",
      cacheBusterParam: "v",
      cacheBuster: null,
      cacheLifetime: 5 * 6e4,
      noCache: isDebug,
      tabletSlickBreakpoint: shoptet.config.breakpoints.lg,
      mobileSlickBreakpoint: shoptet.config.breakpoints.sm
    };
  }();
  var common = /* @__PURE__ */ function() {
    function constant(val) {
      return () => val;
    }
    function identity(val) {
      return val;
    }
    function property(key) {
      return (obj) => obj[key];
    }
    function matcher(keyOrProps, value) {
      if (typeof keyOrProps !== "object")
        return (obj) => obj[keyOrProps] === value;
      return (obj) => Object.entries(keyOrProps).every(([key, value2]) => obj[key] === value2);
    }
    function find(arr, predicate) {
      for (let i = 0; i < arr.length; i++)
        if (predicate(arr[i], i))
          return arr[i];
      return null;
    }
    function findIndex(arr, predicate) {
      for (let i = 0; i < arr.length; i++)
        if (predicate(arr[i], i))
          return i;
      return -1;
    }
    function indexBy(arr, iteratee) {
      var obj = {};
      for (let i = 0; i < arr.length; i++)
        obj[iteratee(arr[i], i)] = arr[i];
      return obj;
    }
    function groupBy(arr, iteratee) {
      const obj = {};
      for (let i = 0; i < arr.length; i++) {
        const key = iteratee(arr[i], i);
        if (!obj[key]) obj[key] = [];
        obj[key].push(arr[i]);
      }
      return obj;
    }
    function isPlainObject(obj) {
      return obj != null && [null, Object.prototype].includes(Object.getPrototypeOf(obj));
    }
    function isString(obj) {
      return Object.prototype.toString.call(obj) === "[object String]";
    }
    function assignDeep(targetObj, ...sourceObjs) {
      for (let sourceObj of sourceObjs) {
        for (let [key, sourceValue] of Object.entries(sourceObj)) {
          if (isPlainObject(targetObj[key])) {
            if (!isPlainObject(sourceValue))
              throw new Error("Cannot extend plain object with value of a different type.");
            assignDeep(targetObj[key], sourceValue);
          } else
            targetObj[key] = sourceValue;
        }
      }
      return targetObj;
    }
    function parseNumber(str) {
      return parseFloat(("" + str).replace(/\s+/g, "").replace(",", "."));
    }
    function parsePrice(str) {
      return parseNumber(("" + str).replace(shoptet.config.currencySymbol, "").replace(/(od|ab|from)\s/, ""));
    }
    function pluralize(n, form1, form24, formMore) {
      n = Math.abs(n);
      return n === 1 ? form1 : n >= 2 && n <= 4 ? form24 : formMore;
    }
    function slugify(str) {
      const letters = str.trim().toLowerCase().split("");
      const transFrom = "\u011B\u0161\u010D\u0159\u017E\xFD\xE1\xED\xE9\u010F\u0165\xF3\xFA\u016F\u0148\u010F\u0165";
      const transTo = "escrzyaiedtouundt";
      for (let index, j = 0; j < letters.length; j++) {
        if ((index = transFrom.indexOf(letters[j])) !== -1)
          letters[j] = transTo[index];
      }
      return letters.join("").replace(/[^a-z0-9]+/g, "-").replace(/-+/g, "-");
    }
    function getProperty(obj, path) {
      return path.split(".").reduce((cur, key) => cur == null ? cur : cur[key], obj);
    }
    function setProperty(obj, path, value) {
      var parts = path.split("."), key;
      while (key = parts.shift()) {
        if (!parts.length)
          obj[key] = value;
        else
          obj = obj[key];
      }
    }
    function once(fn) {
      let done = false;
      return (...args) => {
        if (done)
          return;
        done = true;
        return fn(...args);
      };
    }
    function debounce(fn, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), wait);
      };
    }
    function throttle(fn, wait) {
      let lastCall = 0, lastResult;
      return (...args) => {
        const now = Date.now();
        if (now - lastCall < wait)
          return lastResult;
        lastCall = now;
        return lastResult = fn(...args);
      };
    }
    function nextTick(fn) {
      Promise.resolve().then(fn);
    }
    async function promiseTry(fn) {
      return fn();
    }
    function memoize(cacheKeyResolver, callback, storage = window.sessionStorage) {
      function resolveKey(resolver, args, context) {
        let resolvedKey;
        if (typeof resolver === "function")
          resolvedKey = resolver.apply(context, args);
        else
          resolvedKey = resolver;
        if (Array.isArray(resolvedKey))
          resolvedKey = resolvedKey.map((resolver2) => resolveKey(resolver2, args, context)).join(".");
        else if (!isString(resolvedKey))
          resolvedKey = JSON.stringify(resolvedKey);
        else
          resolvedKey = "" + resolvedKey;
        return resolvedKey;
      }
      const pendingPromises = /* @__PURE__ */ Object.create({});
      return function(...args) {
        const context = this;
        const cacheKey = resolveKey(cacheKeyResolver, args, this);
        if (pendingPromises[cacheKey])
          return pendingPromises[cacheKey];
        let cachedValue = storage?.getItem(cacheKey);
        if (!config.noCache && cachedValue != null) {
          cachedValue = JSON.parse(cachedValue);
          if (cachedValue && cachedValue.t && Date.now() <= cachedValue.t + config.cacheLifetime)
            return Promise.resolve(cachedValue.v);
        }
        const promise = promiseTry(() => callback.apply(context, args)).then(function(result) {
          try {
            if (!config.noCache && cacheKey && storage)
              storage.setItem(cacheKey, JSON.stringify({ v: result, t: Date.now() }));
          } catch (err) {
            console.error("Unable to write cache key '" + cacheKey + "':", err);
          }
          return result;
        });
        pendingPromises[cacheKey] = promise;
        return promise;
      };
    }
    function iteratee$(fn, context) {
      return function() {
        const res = fn.apply(context !== void 0 ? context : this, [$(this), ...arguments]);
        return res instanceof $ ? res.get() : res;
      };
    }
    function resolveValue(value, ...args) {
      return typeof value === "function" ? value(...args) : value;
    }
    return {
      constant,
      identity,
      property,
      matcher,
      isPlainObject,
      isString,
      find,
      findIndex,
      indexBy,
      groupBy,
      iteratee$,
      assignDeep,
      parseNumber,
      parsePrice,
      pluralize,
      slugify,
      getProperty,
      setProperty,
      once,
      throttle,
      debounce,
      nextTick,
      promiseTry,
      memoize,
      resolveValue
    };
  }();
  var helpers = /* @__PURE__ */ function() {
    function getLocaleKey() {
      const dl = getShoptetDataLayer();
      return dl ? dl.language + "." + dl.currency : "";
    }
    function getStrings(moduleConfig) {
      const supportedLangs = Object.keys(moduleConfig.trans || {});
      if (!supportedLangs.length)
        throw new Error("No languages available (config.trans mising or empty)");
      if (supportedLangs.length > 1 && !moduleConfig.fallbackLang)
        throw new Error("config.fallbackLang is not set, but multiple languages are available.");
      const fallbackLang = moduleConfig.fallbackLang || supportedLangs[0];
      const dl = getShoptetDataLayer();
      const lang = dl?.language || fallbackLang;
      return moduleConfig.trans[lang] || moduleConfig.trans[fallbackLang] || {};
    }
    function uuidv4() {
      return ("10000000-1000-4000-8000" + -1e11).replace(
        /[018]/g,
        (c) => (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
      );
    }
    let idCounter = 0;
    function uniqueId(prefix) {
      const id = String(++idCounter);
      return prefix ? prefix + id : id;
    }
    function classNames(...classNames2) {
      return classNames2.flat(Infinity).filter(Boolean).join(" ");
    }
    function classNamesArray(...cns) {
      const str = classNames(cns).trim();
      return str !== "" ? str.split(/\s+/) : [];
    }
    function reactiveToggle(state2, onTrue, onFalse, dom2) {
      let lastValue, onTrue2, onFalse2;
      van.derive(() => {
        const v = Boolean(van.val(state2));
        if (lastValue === v)
          return;
        lastValue = v;
        if (v) {
          common.resolveValue(onTrue2), onTrue2 = null;
          onFalse2 = common.resolveValue(onTrue);
        } else {
          common.resolveValue(onFalse2), onFalse2 = null;
          onTrue2 = common.resolveValue(onFalse);
        }
      }, void 0, dom2);
    }
    return { getLocaleKey, getStrings, uniqueId, uuidv4, classNames, classNamesArray, reactiveToggle };
  }();
  var van = function() {
    const stateProto2 = Object.getPrototypeOf(van_1_5_5_default.state());
    const val = (v) => {
      const protoOfV = Object.getPrototypeOf(v ?? 0);
      if (protoOfV === stateProto2) return v.val;
      if (protoOfV === Function.prototype) return v();
      return v;
    };
    return {
      ...van_1_5_5_default,
      val
    };
  }();
  var dom = /* @__PURE__ */ function() {
    const oneTimeKey = "__tb_oneTime";
    const oneTimeAttrKey = "tbOneTime";
    const oneTimeCommentKey = "tb.oneTimeContents";
    function find(coll, parent = document, copy = true) {
      if (parent == null)
        return [];
      if (!parent.nodeType)
        return find(parent, void 0, true).flatMap((p) => find(coll, p, true));
      if (coll == null)
        return [];
      if (common.isString(coll))
        return copy ? Array.from(parent.querySelectorAll(coll)) : parent.querySelectorAll(coll);
      if (coll.nodeType)
        return [coll];
      if (coll.selector != null)
        return Array.from(coll);
      if (Symbol.iterator in coll)
        return copy ? Array.from(coll) : coll;
      console.log(coll);
      throw new Error(`Invalid tb.dom.find() argument: ${coll}`);
    }
    function first(coll, parent = document) {
      if (parent == null)
        return null;
      if (!parent.nodeType) {
        for (let p of find(parent, void 0, false)) {
          const el2 = first(coll, p);
          if (el2)
            return el2;
        }
        return null;
      }
      if (coll == null)
        return null;
      if (common.isString(coll))
        return parent.querySelector(coll);
      if (coll.nodeType)
        return coll;
      if (coll.selector != null)
        return coll[0] || null;
      if (Symbol.iterator in coll) {
        for (let el2 of coll)
          return el2;
        return null;
      }
      console.log(coll);
      throw new Error(`Invalid tb.dom.first() argument: ${coll}`);
    }
    function appendHelper(parent, fn, ...children) {
      parent = first(parent);
      if (!parent) return;
      parent[fn](van.add(document.createDocumentFragment(), ...children));
    }
    function append(parent, ...children) {
      appendHelper(parent, "append", ...children);
    }
    function prepend(parent, ...children) {
      appendHelper(parent, "prepend", ...children);
    }
    function replaceChildren(parent, ...children) {
      appendHelper(parent, "replaceChildren", ...children);
    }
    function insertReplaceHelper(dom2, fn, ...nodes) {
      dom2 = first(dom2);
      if (!dom2) return;
      const pl = document.createComment("");
      dom2[fn](pl);
      pl.replaceWith(van.add(document.createDocumentFragment(), ...nodes));
    }
    function after(dom2, ...nodes) {
      insertReplaceHelper(dom2, "after", ...nodes);
    }
    function before(dom2, ...nodes) {
      insertReplaceHelper(dom2, "before", ...nodes);
    }
    function replaceWith(dom2, ...nodes) {
      insertReplaceHelper(dom2, "replaceWith", ...nodes);
    }
    function trimHtml(coll, removeBrs = false) {
      for (let el2 of find(coll, void 0, false)) {
        let child;
        while (child = el2.firstChild) {
          if (child.nodeType === Node.TEXT_NODE) {
            const oldValue = child.data;
            const trimmed = oldValue.trimStart();
            if (trimmed === "") {
              child.remove();
              continue;
            } else if (oldValue !== trimmed)
              child.data = trimmed;
          } else if (child.nodeType === Node.COMMENT_NODE || removeBrs && child.tagName === "BR") {
            child.remove();
            continue;
          }
          break;
        }
        while (child = el2.lastChild) {
          if (child.nodeType === Node.TEXT_NODE) {
            const oldValue = child.data;
            const trimmed = oldValue.trimEnd();
            if (trimmed === "") {
              child.remove();
              continue;
            } else if (oldValue !== trimmed)
              child.data = trimmed;
          } else if (child.nodeType === Node.COMMENT_NODE || removeBrs && child.tagName === "BR") {
            child.remove();
            continue;
          }
          break;
        }
      }
    }
    function getTextShallow(coll, noJoin = false) {
      const parts = [];
      for (let el2 of find(coll, void 0, false)) {
        for (let c of el2.childNodes) {
          if (c.nodeType === Node.TEXT_NODE)
            parts.push(c.data);
        }
      }
      return noJoin ? parts : parts.join("");
    }
    function oneTime(coll, key) {
      const fullKey = oneTimeKey + (key ? "__" + key : "");
      let ret = false;
      for (let el2 of find(coll, void 0, false)) {
        if (!el2[fullKey]) {
          el2[fullKey] = true;
          ret = true;
        }
      }
      return ret;
    }
    function oneTimeAttr(coll, key) {
      const fullKey = oneTimeAttrKey + (key || "");
      let ret = false;
      for (let el2 of find(coll, void 0, false)) {
        if (el2.dataset[fullKey] == null) {
          el2.dataset[fullKey] = "";
          ret = true;
        }
      }
      return ret;
    }
    function oneTimeContents(coll, key) {
      const fullKey = oneTimeCommentKey + (key ? "." + key : "");
      let ret = false;
      for (let el2 of find(coll, void 0, false)) {
        let foundComment = false;
        for (let c of el2.childNodes) {
          if (c.nodeType === Node.COMMENT_NODE && c.data === fullKey) {
            foundComment = true;
            break;
          }
        }
        if (!foundComment) {
          el2.prepend(document.createComment(fullKey));
          ret = true;
        }
      }
      return ret;
    }
    function signalNativeChange(coll) {
      for (let el2 of find(coll, void 0, false))
        shoptet.scripts.signalNativeEvent("change", el2);
    }
    function createModuleInitializer(options) {
      if (typeof options === "function")
        options = { domReady: options };
      return common.once(function init(throwInitErrors) {
        try {
          for (let depObj of options.dependencies || []) {
            const { dep, catchInitErrors = false } = depObj.dep ? depObj : { dep: depObj };
            dep.init ? dep.init(!catchInitErrors) : dep();
          }
          if (options.earlyInit && options.earlyInit() === false)
            return;
          if (options.domReady)
            $(() => options.domReady());
          if (options.earlyRender)
            hooks.hookAfterDomLoad(options.earlyRender, true);
          if (options.render)
            hooks.hookAfterDomLoad(options.render, true);
          if (options.earlyRender)
            options.earlyRender();
        } catch (err) {
          const name = options.name || "<anonymous>";
          console.error(`Failed to initialize module ${name}`, err, options);
          if (options.catchInitErrors === false || throwInitErrors && options.catchInitErrors !== true)
            throw err;
          return;
        }
      });
    }
    const whenReady = createModuleInitializer;
    function waitForElement(parent, selector, { subtree = true, jq = true, first: first2 = false } = {}) {
      return new Promise((resolve) => {
        parent = dom.first(parent);
        if (!parent)
          return;
        function check() {
          const found = find(selector, parent, true);
          if (found.length) {
            const found2 = first2 ? found[0] : found;
            resolve(jq ? $(found2) : found2);
          }
          return found.length;
        }
        if (check())
          return;
        new MutationObserver((_entries, observer) => {
          if (check())
            observer.disconnect();
        }).observe(parent, { childList: true, subtree });
      });
    }
    function h(tagName, props, ...children) {
      if (!common.isPlainObject(props)) {
        children.unshift(props);
        props = {};
      }
      const parts = tagName.split(/([\.#]?[a-zA-Z0-9_:-]+)/).filter(Boolean);
      tagName = !parts.length || parts[0].charAt(0) === "." || parts[0].charAt(0) === "#" ? "div" : parts.shift();
      const classList = [];
      for (let part of parts) {
        if (part.charAt(0) === ".")
          classList.push(part.slice(1));
        else if (part.charAt(0) === "#")
          props.id = part.slice(1);
      }
      if (classList.length) {
        if (props.class) {
          const propsClass = props.class;
          props.class = () => helpers.classNames(classList, van.val(propsClass));
        } else
          props.class = classList.join(" ");
      }
      return van.tags[tagName](props, ...children);
    }
    function each(coll, iteratee, parent, filter) {
      if (typeof filter !== "function")
        filter = () => true;
      let i = 0;
      for (let el2 of find(coll, parent, false)) {
        if (filter(el2))
          iteratee(el2, i);
        i++;
      }
    }
    function eachOneTime(coll, iteratee, key, parent) {
      each(coll, iteratee, parent, (el2) => oneTime(el2, key));
    }
    function eachOneTimeAttr(coll, iteratee, key, parent) {
      each(coll, iteratee, parent, (el2) => oneTimeAttr(el2, key));
    }
    function eachOneTimeContents(coll, iteratee, key, parent) {
      each(coll, iteratee, parent, (el2) => oneTimeContents(el2, key));
    }
    function $h2(tagName, attrs, children) {
      if (!common.isPlainObject(attrs)) {
        children = attrs;
        attrs = {};
      }
      const el2 = h(tagName, attrs);
      function appendChildren(coll) {
        if (coll == null)
          return;
        if (common.isString(coll))
          el2.insertAdjacentHTML("beforeend", coll);
        else if (coll.nodeType)
          el2.append(coll);
        else if (coll.selector != null)
          el2.append(...Array.from(coll));
        else if (Array.isArray(coll))
          coll.forEach(appendChildren);
        else {
          console.log(coll);
          throw new Error(`Invalid tb.dom.$h() children: ${coll}`);
        }
      }
      if (children != null) {
        if (Array.isArray(children) || children.selector != null)
          appendChildren(children);
        else
          el2.textContent = "" + children;
      }
      return $(el2);
    }
    function $t2(text) {
      return $(document.createTextNode(text));
    }
    function $each(coll, iteratee) {
      let i = 0;
      for (let el2 of find(coll, void 0, false)) {
        if (iteratee($(el2), i++) === false)
          return false;
      }
    }
    function $eachOneTime($coll, iteratee, key) {
      return $each($coll, ($el, i) => void (oneTime($el, key) && iteratee($el, i)));
    }
    function $eachOneTimeContents($coll, iteratee, key) {
      return $each($coll, ($el, i) => void (oneTimeContents($el, key) && iteratee($el, i)));
    }
    function $map($coll, iteratee, unwrapJq = true) {
      return $($coll).map((i, el2) => {
        const res = iteratee($(el2), i);
        return unwrapJq && res instanceof $ ? res.get() : res;
      });
    }
    function $filter($coll, iteratee) {
      return $($coll).filter((i, el2) => iteratee($(el2), i));
    }
    function createDocumentFromString(html) {
      return new DOMParser().parseFromString(html, "text/html");
    }
    function createFragmentFromString(html) {
      const template = document.createElement("template");
      template.innerHTML = html;
      return template.content;
    }
    return {
      trimHtml,
      getTextShallow,
      find,
      first,
      each,
      eachOneTime,
      eachOneTimeAttr,
      eachOneTimeContents,
      append,
      prepend,
      after,
      before,
      replaceWith,
      replaceChildren,
      oneTime,
      oneTimeAttr,
      oneTimeContents,
      whenReady,
      waitForElement,
      createModuleInitializer,
      h,
      $h: $h2,
      $t: $t2,
      signalNativeChange,
      $each,
      $map,
      $filter,
      $eachOneTime,
      $eachOneTimeContents,
      createDocumentFromString,
      createFragmentFromString
    };
  }();
  var $h = dom.$h;
  var $t = dom.$t;
  var ajax = function() {
    function appendCacheBuster(url, validMs) {
      const value = config.noCache ? String(Date.now()) : `${config.cacheBuster || ""}${validMs ? "." + Math.floor(Date.now() / validMs) * validMs : ""}`;
      return url && value ? `${url}${url.includes("?") ? "&" : "?"}${config.cacheBusterParam}=${encodeURIComponent(value)}` : url;
    }
    async function loadTextNoCache(url) {
      const res = await fetch(url);
      if (!res.ok) {
        const err = new Error(`Failed to fetch ${url}: status = ${res.status}, statusText = ${res.statusText}`);
        err.fetchResponse = res;
        throw err;
      }
      return res.text();
    }
    const loadTextCached = common.memoize((url) => `ajaxCache.${helpers.getLocaleKey()}.${url}`, loadTextNoCache);
    async function loadText(url, options) {
      options = common.isPlainObject(options) ? options : { noCache: options };
      const { noCache = false } = options;
      return noCache ? loadTextNoCache(url) : loadTextCached(url);
    }
    async function loadFragment(url, options) {
      options = common.isPlainObject(options) ? options : { noCache: options, jq: true };
      const { jq = false } = options;
      const body = await loadText(url, options);
      if (jq)
        return $(body, dom.createDocumentFromString(""));
      return dom.createFragmentFromString(body);
    }
    async function loadDocument(url, options) {
      options = common.isPlainObject(options) ? options : { noCache: options, jq: true };
      const { jq = false } = options;
      const doc = new DOMParser().parseFromString(await loadText(url, options), "text/html");
      return jq ? $(doc) : doc;
    }
    async function load(urls, loader, ...loaderArgs) {
      loader = loader || loadDocument;
      if (common.isString(urls))
        return loader(urls);
      if (Array.isArray(urls))
        return Promise.all(urls.map((url) => loader(url, ...loaderArgs)));
      if (common.isPlainObject(urls))
        return Object.fromEntries(await Promise.all(Object.entries(urls).map(async ([key, url]) => [key, await loader(url, ...loaderArgs)])));
      console.log(urls);
      throw new Error(`Invalid tb.ajax.load() argument: ${urls}`);
    }
    function loadSnippets(urls, loader, ...loaderArgs) {
      loader = loader || loadFragment;
      return load(urls, (url, ...restArgs) => loader(config.snippetPath + appendCacheBuster(url), ...restArgs), ...loaderArgs);
    }
    function withSnippets(urls, callback) {
      return function(...args) {
        loadSnippets(urls).then((snippets) => {
          callback.call(this, snippets, ...args);
        });
      };
    }
    function restoreShoptetCookies() {
      const language = getShoptetDataLayer().language;
      const currencyCode = getShoptetDataLayer().currency;
      document.cookie = "language=" + language + "; max-age=31536000; path=/; secure";
      document.cookie = "currencyCode=" + currencyCode + "; max-age=31536000; path=/; secure";
    }
    const ajaxGetCached = loadTextCached;
    return {
      appendCacheBuster,
      ajaxGetCached: loadTextCached,
      loadText,
      loadFragment,
      loadDocument,
      load,
      loadSnippets,
      withSnippets,
      restoreShoptetCookies
    };
  }();
  var hooks = /* @__PURE__ */ function() {
    function hookFn(nameOrFn, callback) {
      const origFn = common.isString(nameOrFn) ? common.getProperty(window, nameOrFn) : nameOrFn;
      if (typeof origFn !== "function") {
        console.error("Failed to hook " + nameOrFn + " (not a function)");
        return;
      }
      function patchedFn() {
        let calledOrigFn = false;
        try {
          return callback(function() {
            calledOrigFn = true;
            return origFn.apply(this, arguments);
          }, arguments, this);
        } catch (err) {
          if (!calledOrigFn) {
            console.error("Patched callback for", nameOrFn, "has thrown an exception before calling origFn: ", err);
            return origFn.apply(this, arguments);
          }
          throw err;
        }
      }
      ;
      patchedFn.origFn = origFn;
      if (common.isString(nameOrFn))
        common.setProperty(window, nameOrFn, patchedFn);
      return patchedFn;
    }
    function hookAfterAjaxDone(url, callback) {
      let urlMatches;
      if (typeof url === "function")
        urlMatches = url;
      else if (url instanceof RegExp)
        urlMatches = (_url) => url.test(_url);
      else
        urlMatches = (_url) => url === _url;
      hookFn("$.ajax", (oldAjax, args, context) => {
        const options = args[0], ajaxResult = oldAjax.apply(context, args);
        if (urlMatches(options?.url, options)) {
          ajaxResult.done = hookFn(ajaxResult.done, (oldDone, args2, context2) => oldDone.apply(context2, [
            ...args2,
            function() {
              try {
                return callback.apply(this, arguments);
              } catch (err) {
                console.error("Ajax callback has thrown an exception:", err);
              }
            }
          ]));
        }
        return ajaxResult;
      });
    }
    function hookAfterDomLoad(callback, native) {
      const events = ["ShoptetDOMContentLoaded", "ShoptetDOMContentChanged", "TDOMContentLoaded"];
      function handler2(e) {
        try {
          callback.call(e ? e.target : null);
        } catch (err) {
          console.error("Dom load callback has thrown an exception:", err);
        }
      }
      for (let event of events)
        document.addEventListener(event, handler2);
      if (native)
        $(() => handler2());
    }
    function signalDomLoad(event, element) {
      let globalEvent = "TDOMContentLoaded";
      if (!event) {
        event = globalEvent;
        globalEvent = null;
      }
      shoptet.scripts.signal(event, element, null, globalEvent);
    }
    let sdlPromise = null;
    function queueSignalDomLoad() {
      sdlPromise ??= Promise.resolve().then(() => {
        sdlPromise = null;
        tb.hooks.signalDomLoad();
      });
    }
    function hookAfterWindowResize(callback, debounceTime = 250, now = true) {
      const $w = $(window);
      function boundCallback() {
        try {
          callback($w.width());
        } catch (err) {
          console.error("Resize callback has thrown an exception:", err);
        }
      }
      $w.on("resize", debounceTime > 0 ? common.debounce(boundCallback, debounceTime) : boundCallback);
      if (now)
        boundCallback();
    }
    function respond(query, onMatch, onUnmatch) {
      const media = matchMedia(query);
      let onUnmatch2, onMatch2, matches = null;
      function safeCall(f) {
        return typeof f === "function" ? f() : void 0;
      }
      function onChange(e) {
        const _matches = e.matches;
        if (matches === _matches)
          return;
        matches = _matches;
        if (matches) {
          safeCall(onMatch2), onMatch2 = null;
          onUnmatch2 = safeCall(onMatch);
        } else {
          safeCall(onUnmatch2), onUnmatch2 = null;
          onMatch2 = safeCall(onUnmatch);
        }
      }
      media.addEventListener("change", onChange);
      onChange(media);
      return media;
    }
    const reactiveMatchMediaCache = {};
    function reactiveMatchMedia(query) {
      return reactiveMatchMediaCache[query instanceof MediaQueryList ? query.media : query] ??= (() => {
        const mediaQueryList = query instanceof MediaQueryList ? query : matchMedia(query);
        const state2 = van.state(mediaQueryList.matches);
        mediaQueryList.addEventListener("change", (e) => {
          state2.val = e.matches;
        });
        return state2;
      })();
    }
    function bindEvents(eventNames, callback, now, $el) {
      $el = $($el || document);
      if (!Array.isArray(eventNames))
        eventNames = [eventNames];
      for (let name of eventNames) {
        if (name === "ready")
          $(callback);
        else
          $el[0].addEventListener(name, callback);
      }
      if (now)
        callback();
    }
    return {
      hookFn,
      hookAfterAjaxDone,
      hookAfterDomLoad,
      signalDomLoad,
      queueSignalDomLoad,
      hookAfterWindowResize,
      reactiveMatchMedia,
      respond,
      bindEvents
    };
  }();
  var sh = /* @__PURE__ */ function() {
    function splitVariantCode(code, asObject) {
      var parts = code.split("-"), res = asObject ? {} : [];
      for (var i = 0; i < parts.length; i += 2) {
        var pair = [
          common.parseNumber(parts[i]),
          common.parseNumber(parts[i + 1])
        ];
        if (asObject)
          res[pair[0]] = pair[1];
        else
          res.push(pair);
      }
      return res;
    }
    function joinVariantCode(pairs) {
      let tempVariantCode = [];
      if (Array.isArray(pairs)) {
        for (let p of pairs)
          tempVariantCode.push(String(p[0]) + "-" + String(p[1]));
      } else {
        for (let key in pairs)
          tempVariantCode.push(String(key) + "-" + String(pairs[key]));
      }
      tempVariantCode.sort();
      return tempVariantCode.join("-");
    }
    function extractVariantCode($el) {
      var className;
      if ($el instanceof $)
        className = $el.attr("class") || "";
      else
        className = $el;
      for (let c of className.split(" ")) {
        let m = c.trim().match(/^([0-9-]+)$/);
        if (m)
          return m[1];
      }
      return null;
    }
    function getNecessaryVariantData(necessaryVariantData, asObject = false) {
      if (necessaryVariantData instanceof $) {
        const script = necessaryVariantData.find(".detail-parameters,.p-variants-block").find("script").html();
        const dataSerialized = script?.match(/shoptet.variantsSplit.necessaryVariantData = (.+);/)?.[1];
        necessaryVariantData = dataSerialized ? JSON.parse(dataSerialized) : {};
      } else if (!common.isPlainObject(necessaryVariantData)) {
        asObject = asObject || Boolean(necessaryVariantData);
        necessaryVariantData = null;
      }
      const variants = necessaryVariantData || shoptet.variantsSplit.necessaryVariantData || {};
      for (let code in variants) {
        const variant = variants[code];
        if (variant.tb_code)
          continue;
        variant.tb_code = code;
        variant.tb_parameterValues = splitVariantCode(code, false);
        variant.tb_parameterValuesObj = splitVariantCode(code, true);
        variant.tb_price = common.parsePrice(variant.price);
        variant.tb_standardPrice = common.parsePrice(variant.standardPrice);
      }
      return asObject ? variants : Object.values(variants);
    }
    function getSelectedParameterValues(form, inclNull = false) {
      form = dom.first(form || "#product-detail-form");
      const parameterValues = {};
      dom.each(".hidden-split-parameter,.split-parameter", (param) => {
        const parameterId = Number(param.dataset.parameterId);
        const valueStr = param.tagName === "SELECT" ? param.value : param.querySelector("input:checked")?.value;
        if (valueStr || inclNull)
          parameterValues[parameterId] = valueStr ? Number(valueStr) : null;
      }, form);
      return parameterValues;
    }
    function convertImageUrl(url, size = "orig") {
      if (!["detail", "thumb", "related", "big", "orig"].includes(size))
        throw new Error(`Invalid image size: ${size}`);
      return url.replace(/\/(detail|thumb|related|orig|big)\//, `/${size}/`);
    }
    function getDescriptiveParameters(detail, asObject = false) {
      detail = dom.first(detail || ".p-detail");
      const params = [], paramsObj = {};
      dom.each(".extended-description .detail-parameters tr", (tr) => {
        const name = dom.getTextShallow(tr.querySelector(".row-header-label")).trim();
        const td = tr.querySelector("td");
        if (name)
          params.push(params[name] = { tr, $tr: $(tr), name, td, value: (td?.textContent || "").trim() });
      }, detail);
      return asObject ? paramsObj : params;
    }
    function normalizeProductUrl(url) {
      if (!common.isString(url))
        return null;
      url = url.toLowerCase().trim();
      if (url === "")
        return null;
      if (!url.match(/^(http|\/)/))
        url = "/" + url;
      try {
        url = new URL(url, location.href).pathname;
        return url.endsWith("/") ? url : url + "/";
      } catch (err) {
        return null;
      }
    }
    return {
      splitVariantCode,
      joinVariantCode,
      extractVariantCode,
      getNecessaryVariantData,
      getSelectedParameterValues,
      getDescriptiveParameters,
      convertImageUrl,
      normalizeProductUrl
    };
  }();
  var ui = function() {
    function wrapColorboxHtml($elOrHtml) {
      if (typeof $elOrHtml === "string")
        return shoptet.content.colorboxHeader + $elOrHtml + shoptet.content.colorboxFooter;
      const wrapHtml = shoptet.content.colorboxHeader + shoptet.content.colorboxFooter;
      return wrapHtml ? $(wrapHtml).html($elOrHtml) : $($elOrHtml);
    }
    async function loadAndOpenModal(url, selectorOrTransformer, optionsOrSize = {}) {
      const transformer = typeof selectorOrTransformer === "string" ? ($page2) => $page2.find(selectorOrTransformer) : selectorOrTransformer;
      const options = typeof optionsOrSize === "string" ? { size: optionsOrSize } : optionsOrSize || {};
      const size = !options.size && options.size !== null ? "lg" : options.size;
      delete options.size;
      if (size !== null) {
        const sizeSuffix = size.slice(0, 1).toUpperCase() + size.slice(1).toLowerCase();
        if (options.width === void 0)
          options.width = shoptet.modal.config["width" + sizeSuffix];
        options.className = `${shoptet.modal.config["class" + sizeSuffix]} ${options.className || ""}`;
      }
      const $page = await ajax.loadDocument(url);
      const $content = $(transformer($page));
      if (!$content.length)
        throw new Error(`Element not found (url: ${url})`);
      return new Promise((resolve) => {
        shoptet.modal.open({
          ...options,
          html: wrapColorboxHtml($content),
          onComplete() {
            if (options.onComplete?.() !== false) {
              shoptet.images.unveil();
              $("body").removeClass(shoptet.config.bodyClasses);
              hideSpinner();
              setTimeout(() => {
                shoptet.modal.shoptetResize();
              }, 1);
              hooks.signalDomLoad();
            }
            resolve();
          }
        });
      });
    }
    function responsiveSlick(breakpoint, els, options) {
      els = dom.find(els).filter((el2) => dom.oneTime(el2, "responsiveSlick"));
      if (typeof breakpoint !== "function") {
        const maxWidth = breakpoint;
        breakpoint = function(width) {
          return width <= maxWidth;
        };
      }
      let oldVisible = null;
      hooks.hookAfterWindowResize(() => {
        const visible = !!breakpoint($(window).width());
        if (visible === oldVisible)
          return;
        els = els.filter((el2) => el2.isConnected);
        if (!els.length)
          return;
        try {
          if (visible)
            $(el).slick(options);
          else if (oldVisible)
            $(el).slick("destroy");
        } finally {
          oldVisible = visible;
        }
      });
    }
    const tabletSlick = responsiveSlick.bind(null, config.tabletSlickBreakpoint);
    const mobileSlick = responsiveSlick.bind(null, config.mobileSlickBreakpoint);
    return {
      wrapColorboxHtml,
      loadAndOpenModal,
      responsiveSlick,
      tabletSlick,
      mobileSlick
    };
  }();
  var JsonStorage = class _JsonStorage {
    constructor(key, defaultFactory = () => ({}), backend = window.sessionStorage) {
      if (!common.isString(key))
        throw new Error("Storage key must be a string.");
      this.key = key;
      this.defaultFactory = defaultFactory;
      this.backend = backend;
    }
    static local(key, defaultFactory = () => ({})) {
      return new _JsonStorage(key, defaultFactory, window.localStorage);
    }
    static session(key, defaultFactory = () => ({})) {
      return new _JsonStorage(key, defaultFactory, window.sessionStorage);
    }
    get() {
      const rawValue = this.backend.getItem(this.key);
      return rawValue === null ? this.defaultFactory() : JSON.parse(rawValue);
    }
    set(obj) {
      this.backend.setItem(this.key, JSON.stringify(obj));
    }
    clear() {
      this.backend.removeItem(this.key);
    }
    updateAndGet(f) {
      const obj = f(this.get());
      this.set(obj);
      return obj;
    }
    /** @deprecated */
    getAndUpdate(f) {
      return this.updateAndGet(f);
    }
  };
  var toolbox = {
    config,
    common,
    helpers,
    van,
    dom,
    $h,
    $t,
    ajax,
    hooks,
    sh,
    ui,
    JsonStorage
  };
  if (window.toolbox)
    console.error("toolbox is already loaded, original:", window.toolbox);
  window.toolbox = window.tb = toolbox;

  // dist/modules-v2.js
  (function(tb2) {
    if (!tb2)
      throw new Error("toolbox is not loaded");
    const AdvancedOrderHooks = function() {
      const filters = [];
      function addContentFilter(callback) {
        filters.push(callback);
      }
      function isPromise(obj) {
        return obj && obj.then;
      }
      function transformAdvancedOrderContent(content, $lastForm2) {
        return filters.reduce(function(contentOrPromise, filter) {
          if (isPromise(contentOrPromise))
            return Promise.resolve(contentOrPromise).then((content2) => filter(content2, $lastForm2));
          return filter(contentOrPromise, $lastForm2);
        }, content);
      }
      let $lastForm = $();
      const init = tb2.dom.createModuleInitializer({
        earlyInit() {
          tb2.hooks.hookFn("shoptet.tracking.handleAction", function(handleAction, args, context) {
            $lastForm = $(args[0]);
            return handleAction.apply(context, args);
          });
          tb2.hooks.hookFn("shoptet.ajax.makeAjaxRequest", function(makeAjaxRequest, args, context) {
            const url = args[0], callbacks = args[3];
            if (typeof url === "string" && url.startsWith(shoptet.config.advancedOrderUrl))
              callbacks.success = tb2.hooks.hookFn(callbacks.success, function(success, args2, context2) {
                const response = args2[0];
                const payload = response.getPayload();
                if (!payload.content)
                  return success.apply(context2, args2);
                let contentOrPromise = transformAdvancedOrderContent(payload.content, $lastForm);
                if (isPromise(contentOrPromise))
                  return Promise.resolve(contentOrPromise).then(function(content) {
                    payload.content = content;
                    success.apply(context2, args2);
                  });
                payload.content = contentOrPromise;
                return success.apply(context2, args2);
              });
            return makeAjaxRequest.apply(context, args);
          });
        }
      });
      return { init, addContentFilter };
    }();
    const AjaxSubmitFormHooks = function() {
      let ajaxSubmitFormArgs;
      const callbackTransformers = [];
      function ajaxSubmitForm(action, form, callingFunctions, replaceContent, displaySpinner, _transformCallbacks) {
        if (_transformCallbacks)
          callbackTransformers.push(_transformCallbacks);
        shoptet.cart.ajaxSubmitForm(action, form, callingFunctions, replaceContent, displaySpinner);
        if (_transformCallbacks)
          callbackTransformers.splice(callbackTransformers.indexOf(_transformCallbacks), 1);
      }
      function ajaxSubmitFormAsync(action, form, callingFunctions, replaceContent, displaySpinner) {
        return new Promise((resolve, reject) => {
          AjaxSubmitFormHooks.ajaxSubmitForm(action, form, callingFunctions, replaceContent, displaySpinner, (callbacks) => {
            callbacks.success = tb2.hooks.hookFn(callbacks.success, (success, args, context) => {
              const res = success.apply(context, args);
              resolve(args[0]);
              return res;
            });
            callbacks.failed = tb2.hooks.hookFn(callbacks.failed, (failed, args, context) => {
              const res = failed.apply(context, args);
              reject(args[0]);
              return res;
            });
          });
        });
      }
      function hookAjaxSubmitFormCallbacks(transformCallbacks) {
        callbackTransformers.push((callbacks, args) => args && transformCallbacks(callbacks, args));
      }
      function hookAjaxSubmitFormSuccess(callback) {
        hookAjaxSubmitFormCallbacks((callbacks, ajaxSubmitFormArgs2) => {
          callbacks.success = tb2.hooks.hookFn(callbacks.success, (success, args, context) => {
            callback(() => success.apply(context, args), args[0], ajaxSubmitFormArgs2);
          });
        });
      }
      const init = tb2.dom.createModuleInitializer({
        earlyInit() {
          tb2.hooks.hookFn("shoptet.cart.ajaxSubmitForm", function(ajaxSubmitForm2, args, context) {
            ajaxSubmitFormArgs = args;
            ajaxSubmitForm2.apply(context, args);
            ajaxSubmitFormArgs = null;
          });
          tb2.hooks.hookFn("shoptet.ajax.makeAjaxRequest", function(makeAjaxRequest, args, context) {
            var callbacks = args[3];
            for (let transformCallbacks of callbackTransformers)
              transformCallbacks(callbacks, ajaxSubmitFormArgs);
            return makeAjaxRequest.apply(context, args);
          });
        }
      });
      return { init, ajaxSubmitForm, ajaxSubmitFormAsync, hookAjaxSubmitFormCallbacks, hookAjaxSubmitFormSuccess };
    }();
    const CarrierRemark = function($h2, $t2) {
      const config2 = {
        maxLength: 50,
        rows: 4,
        fallbackLang: "cs",
        trans: {
          cs: {
            remarkPlaceholder: "Va\u0161e pozn\xE1mka pro dopravce",
            remarkCheckboxLabel: "Zadat pozn\xE1mku pro dopravce"
          },
          sk: {
            remarkPlaceholder: "Va\u0161a pozn\xE1mka pre dopravcov",
            remarkCheckboxLabel: "Zada\u0165 pozn\xE1mku pre dopravcov"
          },
          de: {
            remarkPlaceholder: "Ihr Hinweis an die Bef\xF6rderer",
            remarkCheckboxLabel: "Hinweise zur Lieferung"
          }
        }
      };
      function bindTextarea($ta) {
        $("#order-form").on("ShoptetSuccessfulValidation", function(e) {
          var $remark = $("#remark"), remark = $remark.val().trim(), carrierRemark = $ta.val().trim();
          var parts = [];
          if (remark !== "")
            parts.push("# Pozn\xE1mka\n" + remark);
          if (carrierRemark !== "")
            parts.push("# Pozn\xE1mka pro dopravce\n" + carrierRemark);
          $remark.val(parts.join("\n\n"));
        });
      }
      function createRemarkElements() {
        var $ta = $h2("textarea#carrier-remark.form-control", {
          placeholder: t.remarkPlaceholder,
          maxlength: String(config2.maxLength),
          rows: String(config2.rows)
        });
        bindTextarea($ta);
        return [
          $h2(".form-group", [
            $h2("input#add-carrier-note", {
              type: "checkbox",
              "data-unveil": "carrier-note"
            }),
            $h2("label.whole-width", {
              "for": "add-carrier-note"
            }, t.remarkCheckboxLabel)
          ]),
          $h2("div#carrier-note.js-hidden", [
            $h2("fieldset.box.box-sm.box-bg-default", [
              $ta
            ])
          ])
        ];
      }
      let t;
      const init = tb2.dom.createModuleInitializer({
        earlyInit() {
          t = tb2.helpers.getStrings(config2);
          if (getShoptetDataLayer("pageType") !== "customerDetails")
            return false;
        },
        earlyRender() {
          tb2.dom.$eachOneTime("#note", ($note) => {
            $note.after(createRemarkElements());
          }, "CarrierRemark");
        }
      });
      return { config: config2, init };
    }(tb2.$h, tb2.$t);
    const CartItemData = function($h2) {
      const config2 = {
        storageKey: "cartItemData",
        compareCallback: function(a, b) {
          return a === b;
        },
        cloneCallback: function(item) {
          return JSON.parse(JSON.stringify(item));
        },
        fallbackLang: "cs",
        trans: {
          cs: {
            quantityLocked: "Mno\u017Estv\xED nelze m\u011Bnit, proto\u017Ee k polo\u017Ece jsou p\u0159ipojena dodate\u010Dn\xE1 data"
          },
          sk: {
            quantityLocked: "Mno\u017Estvo nie je mo\u017En\xE9 meni\u0165, preto\u017Ee k polo\u017Eke s\xFA pripojen\xE9 dodato\u010Dn\xE9 d\xE1ta"
          },
          en: {
            quantityLocked: "The quantity cannot be changed because the item has additional data attached"
          }
        }
      };
      var addToCartSuccessCallback;
      function isCartEmpty() {
        var cart = getShoptetDataLayer("cart");
        return cart && !cart.length;
      }
      function getStoredItems() {
        return JSON.parse(localStorage.getItem(config2.storageKey) || "[]");
      }
      function setStoredItems(items) {
        localStorage.setItem(config2.storageKey, JSON.stringify(items));
      }
      function generateUid() {
        return Date.now().toString(36) + Math.random().toString(36).slice(2);
      }
      function saveCustomProductData(productData, amount, customData) {
        var item = {
          uid: generateUid(),
          productData,
          amount,
          customData
        };
        var items = getStoredItems();
        items.push(item);
        setStoredItems(items);
        return item;
      }
      function cleanupStoredItems() {
        if (isCartEmpty())
          localStorage.removeItem(config2.storageKey);
        var items = getStoredItems(), dirty = false;
        for (let item of items)
          if (!item.uid) {
            item.uid = generateUid();
            dirty = true;
          }
        if (dirty)
          setStoredItems(items);
      }
      function extractCurrentAmount($el) {
        return tb2.common.parseNumber($el.find("input[name=amount]").val());
      }
      function extractProductDataFromDetail($detail) {
        var formData = $detail.find("#product-detail-form").serializeArray(), formDataObj = tb2.common.indexBy(formData, tb2.common.property("name"));
        var productId = tb2.common.parseNumber(formDataObj.productId.value), priceId = tb2.common.parseNumber(formDataObj.priceId.value), identifier = $detail.find("meta[itemprop=identifier]").attr("content"), sku = $detail.find(".p-code > span:not(:first-child):not(.noDisplay)").text().trim() || void 0, surcharges = {}, surchargesArr = [];
        $detail.find("select.surcharge-parameter").each(function() {
          var parameterName = $(this).data("parameterName");
          var $selected = $(this).find(":selected");
          if ($selected.val() === "")
            surcharges[parameterName] = "";
          else {
            const selectedText = $selected.text().split(/ \+(?:[0-9,.]+.+|.+?[0-9,.]+)$/)[0];
            surcharges[parameterName] = selectedText;
            surchargesArr.push(parameterName + " - " + selectedText);
          }
        });
        return { productId, priceId, identifier, sku, surcharges, surchargesArr };
      }
      function extractProductDataFromCartWidgetRow($row) {
        var identifier = $row.data("microIdentifier"), sku = $row.attr("data-micro-sku"), priceId = tb2.common.parseNumber($row.find("input[name=priceId]").val());
        return { priceId, identifier, sku };
      }
      function _extractSurchargesHelper($row) {
        const $surcharges = $row.find(".main-link-surcharges");
        if (!$surcharges.length)
          return {};
        const surcharges = {};
        const pairs = $surcharges.text().trim().replace(/^[^:]+: /, "").split(/, (?=[\p{Lu}\p{N}])/u);
        for (let p of pairs) {
          const pair = p.split(/ - (?=[\p{Lu}\p{N}])/u);
          surcharges[pair[0]] = pair[1];
        }
        return surcharges;
      }
      function _extractSurchargesArrHelper($row) {
        const $surcharges = $row.find(".main-link-surcharges");
        if (!$surcharges.length)
          return [];
        return $surcharges.text().trim().replace(/^[^:]+: /, "").split(/, (?=[\p{Lu}\p{N}])/u);
      }
      function extractProductDataFromCartTableRow($row) {
        var identifier = $row.data("microIdentifier"), sku = $row.attr("data-micro-sku"), priceId = tb2.common.parseNumber($row.find("input[name=priceId]").val()), surcharges = _extractSurchargesHelper($row), surchargesArr = _extractSurchargesArrHelper($row);
        return { priceId, identifier, sku, surcharges, surchargesArr };
      }
      function extractProductDataFromOrderSummaryRow($row) {
        var identifier = $row.data("microIdentifier"), sku = $row.attr("data-micro-sku"), surcharges = _extractSurchargesHelper($row), surchargesArr = _extractSurchargesArrHelper($row);
        return { identifier, sku, surcharges, surchargesArr };
      }
      function addProductToCart(payload, customData) {
      }
      function addTriggerInputToForm($form) {
        return $h2("input", { type: "hidden", name: "__hasCustomData", value: "1" }).appendTo($form);
      }
      function setupDetailCustomData($detail, customData) {
        var $form = $detail.find("#product-detail-form"), $triggerInput = addTriggerInputToForm($form), productData = extractProductDataFromDetail($detail), amount = extractCurrentAmount($form);
        addToCartSuccessCallback = function(response) {
          saveCustomProductData(productData, amount, customData);
        };
        setTimeout(function() {
          $triggerInput.remove();
          addToCartSuccessCallback = null;
        }, 50);
      }
      function addDetailProductToCart($detail, customData) {
        var $form = $detail.find("#product-detail-form");
        setupDetailCustomData($detail, customData);
        $form.trigger("submit");
      }
      function itemProductDataMatcher(productData) {
        function compareValues(key, a, b, useSurchargesArr) {
          if (key === "surcharges") {
            if (useSurchargesArr)
              return true;
            for (let k in a) {
              if (a[k] !== b[k] && !(a[k] === "" && b[k] === void 0))
                return false;
            }
            for (let k in b) {
              if (b[k] !== a[k] && !(b[k] === "" && a[k] === void 0))
                return false;
            }
            return true;
          } else if (key === "surchargesArr") {
            if (!useSurchargesArr)
              return false;
            return a.length === b.length && a.every((v) => b.includes(v));
          } else {
            return a === b;
          }
        }
        return function(item) {
          const useSurchargesArr = item.productData.surchargesArr && productData.surchargesArr;
          for (let key in item.productData) {
            if (productData[key] !== void 0 && !compareValues(key, productData[key], item.productData[key], useSurchargesArr))
              return false;
          }
          for (let key in productData) {
            if (item.productData[key] !== void 0 && !compareValues(key, productData[key], item.productData[key], useSurchargesArr))
              return false;
          }
          return true;
        };
      }
      function getMatchingCustomItemsForProduct(productData) {
        var items = getStoredItems();
        return items.filter(itemProductDataMatcher(productData));
      }
      function lockCartRowQuantity($row) {
        if (!t)
          t = tb2.helpers.getStrings(config2);
        const $quantity = $($row).find(".quantity");
        $quantity.addClass("readonly");
        $quantity.find(".increase, .decrease").addClass("hidden");
        $quantity.find("input").prop("readonly", true);
        $quantity.attr("title", t.quantityLocked);
      }
      function restrictCartWidget() {
        var $cartWidget = $(".cart-widget");
        if (!$cartWidget.length)
          return;
        $cartWidget.find(".cart-widget-product").each(function() {
          var $row = $(this), info = extractProductDataFromCartWidgetRow($row);
          if (getMatchingCustomItemsForProduct(info).length)
            lockCartRowQuantity($row);
        });
      }
      function cloneCustomItem(item) {
        return {
          uid: null,
          originalItem: item.originalItem || item,
          productData: item.productData,
          amount: item.amount,
          customData: config2.cloneCallback(item.customData)
        };
      }
      function linkCustomItemsToRows(rows) {
        var remainingItems = getStoredItems().slice();
        for (let row of rows) {
          let matcher = itemProductDataMatcher(row.productData), matchingItems = [], amount = row.amount;
          for (let i = remainingItems.length; amount > 0 && i-- > 0; ) {
            let item = remainingItems[i];
            if (!matcher(item))
              continue;
            if (item.amount <= amount) {
              matchingItems.push(item);
              amount -= item.amount;
              remainingItems.splice(i, 1);
            } else {
              let clone = cloneCustomItem(item);
              clone.amount = amount;
              matchingItems.push(clone);
              item.amount -= amount;
              amount = 0;
            }
          }
          row.matchingItems = matchingItems;
          row.amountLeft = amount;
          if (row.$el) {
            row.$el.data("productData", row.productData);
            row.$el.data("amount", row.amount);
            row.$el.data("matchingItems", row.matchingItems);
            row.$el.data("amountLeft", row.amountLeft);
          }
        }
        if (remainingItems.length)
          console.warn("Remaining items:", remainingItems);
        return rows;
      }
      function linkCartTableRows($rows) {
        var rows = $rows.map(function() {
          var $t2 = $(this);
          return {
            $el: $t2,
            productData: extractProductDataFromCartTableRow($t2),
            amount: extractCurrentAmount($t2)
          };
        }).toArray();
        return linkCustomItemsToRows(rows);
      }
      function linkOrderSummaryRows($rows) {
        var rows = $rows.map(function() {
          var $t2 = $(this);
          return {
            $el: $t2,
            productData: extractProductDataFromOrderSummaryRow($t2),
            amount: tb2.common.parseNumber($t2.find(".cart-item-amount").text())
          };
        }).toArray();
        return linkCustomItemsToRows(rows);
      }
      function appendCustomRows(rows, renderCallback) {
        for (let row of rows) {
          if (!row.matchingItems.length)
            continue;
          let $last = row.$el;
          for (let item of row.matchingItems) {
            let $r = renderCallback(item, row.$el, row);
            if (!$r || !$($r).length)
              continue;
            $last = $($r).data("matchingItem", item).data("row", row).insertAfter($last);
          }
        }
      }
      function reduceCartRowAmount($row, amount) {
        var currentAmount = extractCurrentAmount($row);
        var $updateForm;
        if (currentAmount > amount) {
          var $input = $row.find("input[name=amount]");
          $input.val(currentAmount - amount);
          $updateForm = $input.closest("form");
        } else {
          $updateForm = $row.find(".remove-item").closest("form");
        }
        shoptet.cart.ajaxSubmitForm($updateForm.attr("action"), $updateForm[0], "functionsForCart", "cart", true);
      }
      function removeCustomItem(item, row, removeFromCart) {
        var uid = item.originalItem ? item.originalItem.uid : item.uid;
        if (removeFromCart && row.$el)
          reduceCartRowAmount(row.$el, item.amount);
        var items = getStoredItems(), index = tb2.common.findIndex(items, function(item2) {
          return item2.uid === uid;
        });
        if (index === -1)
          return false;
        items[index].amount -= item.amount;
        if (items[index].amount <= 0)
          items.splice(index, 1);
        setStoredItems(items);
      }
      let t;
      const init = tb2.dom.createModuleInitializer({
        earlyInit() {
          t = tb2.helpers.getStrings(config2);
          tb2.hooks.hookFn("shoptet.tracking.handleAction", function(handleAction, args, context) {
            const form = args[0], response = args[1];
            handleAction.apply(context, args);
            if (shoptet.tracking.getFormAction(form.getAttribute("action")) && response.getFromPayload("cartItems") && !response.getFromPayload("cartItems").length)
              cleanupStoredItems();
          });
          cleanupStoredItems();
          tb2.hooks.hookFn("shoptet.ajax.makeAjaxRequest", function(makeAjaxRequest, args, context) {
            var data = args[2], callbacks = args[3];
            if (typeof data === "string" && data.indexOf("__hasCustomData") !== -1) {
              var _addToCartSuccessCallback = addToCartSuccessCallback;
              addToCartSuccessCallback = null;
              callbacks.success = tb2.hooks.hookFn(callbacks.success, function(success, args2, context2) {
                var response = args2[0];
                if (_addToCartSuccessCallback)
                  _addToCartSuccessCallback(response);
                return success.apply(context2, args2);
              });
            }
            return makeAjaxRequest.apply(context, args);
          });
        },
        earlyRender() {
          restrictCartWidget();
        }
      });
      return {
        init,
        extractProductDataFromDetail,
        extractProductDataFromCartTableRow,
        extractProductDataFromCartWidgetRow,
        extractProductDataFromOrderSummaryRow,
        extractCurrentAmount,
        addProductToCart,
        addDetailProductToCart,
        setupDetailCustomData,
        linkCustomItemsToRows,
        linkCartTableRows,
        linkOrderSummaryRows,
        appendCustomRows,
        removeCustomItem,
        lockCartRowQuantity,
        getStoredItems
      };
    }(tb2.$h);
    const DelayedCart = function() {
      const config2 = {
        timeout: 500
      };
      const init = tb2.dom.createModuleInitializer({
        earlyInit() {
          var showCartTimeout = null;
          function reset() {
            clearTimeout(showCartTimeout);
            showCartTimeout = null;
          }
          tb2.hooks.hookFn("shoptet.global.showPopupWindow", function(showPopupWindow, args, context) {
            var target = args[0], show = args[1];
            if (target === "cart" && show) {
              if (showCartTimeout)
                return;
              showCartTimeout = setTimeout(function() {
                reset();
                showPopupWindow.apply(context, args);
              }, config2.timeout);
              return;
            }
            if (!show)
              reset();
            return showPopupWindow.apply(context, args);
          });
          $(function() {
            $("html").on("mouseleave", '.toggle-window[data-hover="true"]', reset);
          });
        }
      });
      return { config: config2, init };
    }();
    const DetailHooks = function() {
      let detailLoaded = false;
      function hookAfterVariantChange(callback) {
        document.addEventListener("TVariantChanged", callback);
        if (detailLoaded)
          callback();
      }
      function hookAfterQuantityChange(callback) {
        document.addEventListener("TQuantityChanged", callback);
        if (detailLoaded)
          callback();
      }
      function signalVariantChange() {
        shoptet.scripts.signal("TVariantChanged", null, null, null);
      }
      function signalQuantityChange() {
        shoptet.scripts.signal("TQuantityChanged", null, null, null);
      }
      const init = tb2.dom.createModuleInitializer({
        earlyInit() {
          tb2.hooks.hookFn("shoptet.surcharges.updatePrices", function(updatePrices, args, context) {
            updatePrices.apply(context, args);
            signalVariantChange();
          });
          for (let ev of ["ShoptetVariantUnavailable", "ShoptetVariantAvailable"])
            document.addEventListener(ev, () => {
              signalVariantChange();
              signalQuantityChange();
            });
          let resetTimeout;
          document.addEventListener("ShoptetSelectedParametersReset", (e) => {
            clearTimeout(resetTimeout);
            resetTimeout = setTimeout(signalVariantChange, 0);
          });
          tb2.hooks.hookFn("shoptet.helpers.updateQuantity", (updateQuantity, args, context) => {
            const el2 = args[0];
            if ($(el2).closest("#product-detail-form,.product-detail-form").length) {
              args[5] = tb2.hooks.hookFn(args[5] || (() => {
              }), (origCallback, args2, context2) => {
                signalQuantityChange();
                return origCallback.apply(context2, args2);
              });
            }
            return updateQuantity.apply(context, args);
          });
          $("body").on("change input", "input[name=amount]", function(e) {
            if ($(this).closest("#product-detail-form,.product-detail-form").length) {
              signalQuantityChange();
            }
          });
          if ($("body.type-detail").length) {
            signalVariantChange();
            signalQuantityChange();
            detailLoaded = true;
          }
        }
      });
      return { init, signalVariantChange, hookAfterVariantChange, signalQuantityChange, hookAfterQuantityChange };
    }();
    const ExternalRenderEvents = function() {
      const config2 = {
        navstiveneProdukty: false,
        shoptetModal: true,
        luigisBox: false,
        premiumCarousel: false,
        countdown: false,
        sideCart: false,
        shippingDate: false,
        upsell: false,
        mehubBonusCart: false,
        satisflowReview: false,
        dkLabComparer: false,
        tuito: false,
        quickView: false,
        ratingPlus: false
      };
      function signalDomLoadAfter(name) {
        if (typeof name === "string" && !tb2.common.getProperty(window, name))
          return;
        return tb2.hooks.hookFn(name, (origFn, args, context) => {
          origFn.apply(context, args);
          tb2.hooks.signalDomLoad();
        });
      }
      const hooks2 = {
        navstiveneProdukty() {
          signalDomLoadAfter("window.showVisitedProducts");
          document.addEventListener("dkLabLastVisitedProductsChanged", () => void tb2.hooks.signalDomLoad());
        },
        shoptetModal() {
          tb2.hooks.hookFn("shoptet.modal.open", (open, args, context) => {
            const options = args[0];
            return open.call(context, {
              ...options,
              onComplete: signalDomLoadAfter(options.onComplete || (() => {
              }))
            });
          });
        },
        luigisBox() {
          $("html").on("init", ".slick-slider", function() {
            if ($(this).find(".lb-product").length)
              tb2.hooks.signalDomLoad();
          });
        },
        premiumCarousel() {
          tb2.hooks.hookFn("$.fn.mouseenter", function(mouseenter, args, context) {
            mouseenter.apply(context, args);
            if (context.is(".carousel-navigation"))
              tb2.hooks.signalDomLoad();
            return context;
          });
        },
        countdown() {
          signalDomLoadAfter("window.compareGuids");
          signalDomLoadAfter("window.compareGuid");
        },
        sideCart() {
          signalDomLoadAfter("window.pkSuccessCallback");
        },
        shippingDate() {
          signalDomLoadAfter("window.fvstudio_shippingdate.checkTimepickerAvailability");
        },
        upsell() {
          function hookObject(obj) {
            if (!obj || !obj.runActually)
              return;
            let m = obj.runActually.toString().match(/API_URL = '(.+?)'/);
            if (!m)
              return;
            let API_URL = JSON.parse('"' + m[1] + '"');
            tb2.hooks.hookFn("$.ajax", function(ajax2, args, context) {
              let options = args[0];
              if (options.url === API_URL + "/data")
                options.success = tb2.hooks.hookFn(options.success, function(success, args2, context2) {
                  success.apply(context2, args2);
                  tb2.hooks.signalDomLoad();
                });
              return ajax2.apply(context, args);
            });
          }
          hookObject(window.fvstudio);
          hookObject(window.fvstudio_dev);
          signalDomLoadAfter("window.fvStudioAppCartUpsell.onloaded");
        },
        mehubBonusCart() {
          if (!$(".in-kosik").length)
            return;
          setInterval(function() {
            if (tb2.dom.oneTime(".mehub-bonus-cart-container"))
              tb2.hooks.signalDomLoad();
          }, 500);
        },
        satisflowReview() {
          signalDomLoadAfter("window.satisflowReviewInitModalReviewImage");
        },
        dkLabComparer() {
          signalDomLoadAfter("window.dkLabProductComparer.showNumberOfComparedItems");
          signalDomLoadAfter("window.dkLabProductComparer.showDetailControlsDkLabComparer");
          signalDomLoadAfter("window.dkLabProductComparer.showCategoryControlsDkLabComparer");
        },
        tuito() {
          const $infoWrapper = $(".p-detail .p-info-wrapper");
          if (!$infoWrapper.length)
            return;
          const obs = new MutationObserver((mutationList) => {
            for (let rec of mutationList) {
              if ($(rec.addedNodes).filter(".tuito-data-table").length)
                tb2.hooks.signalDomLoad();
            }
          });
          obs.observe($infoWrapper[0], { subtree: true, childList: true });
        },
        quickView() {
          const obs = new MutationObserver(() => {
            if (tb2.dom.oneTime(".quick-box-content .p-detail", "ExternalRenderEvents"))
              tb2.hooks.signalDomLoad();
          });
          obs.observe(document.body, { subtree: true, childList: true });
        },
        ratingPlus() {
          document.addEventListener("RatingPlusDone", () => {
            tb2.hooks.signalDomLoad();
          });
        }
      };
      const init = tb2.dom.createModuleInitializer({
        earlyInit() {
          for (let hookName in hooks2) {
            if (hooks2.hasOwnProperty(hookName) && config2[hookName])
              hooks2[hookName]();
          }
        }
      });
      return { config: config2, init };
    }();
    const FavouriteProducts = function($h2) {
      const config2 = {
        storageKey: "favouriteProducts",
        productBlockSelector: ".product .p",
        callbackPreset: "clean",
        addBtnToDetailCallback: null,
        addBtnToProductBlockCallback: null,
        maxPriceLifetime: -1,
        wrapperClass: "",
        listClass: "products products-block products-favourite",
        itemClass: "product",
        renderOptions: {
          alwaysShowDetailBtn: true,
          showAvailability: false
        },
        fallbackLang: "cs",
        trans: {
          cs: {
            favouritePageSelector: "body.in-oblibene",
            blockHeadingOuterHtml: tb2.$h("h4", "Obl\xEDben\xE9 produkty"),
            emptyNoticeHtml: "V\xE1\u0161 seznam je bohu\u017Eel pr\xE1zdn\xFD.",
            copyLinkBtnHtml: "Kop\xEDrovat odkaz",
            copyLinkBtnCopiedHtml: "Zkop\xEDrov\xE1no",
            copyLinkLabelHtml: "Odkaz pro sd\xEDlen\xED",
            addBtnHtml: "P\u0159idat do obl\xEDben\xFDch",
            removeBtnHtml: "Smazat z obl\xEDben\xFDch"
          },
          sk: {
            favouritePageSelector: "body.in-oblibene,body.in-oblubene",
            blockHeadingOuterHtml: tb2.$h("h4", "Ob\u013E\xFAben\xE9 produkty"),
            emptyNoticeHtml: "V\xE1\u0161 zoznam je bohu\u017Eia\u013E pr\xE1zdny.",
            copyLinkBtnHtml: "Kop\xEDrova\u0165 odkaz",
            copyLinkBtnCopiedHtml: "Skop\xEDrovan\xE9",
            copyLinkLabelHtml: "Odkaz na zdie\u013Eanie",
            addBtnHtml: "Prida\u0165 do ob\u013E\xFAben\xFDch",
            removeBtnHtml: "Zmaza\u0165 z ob\u013E\xFAben\xFDch"
          },
          en: {
            favouritePageSelector: "body.in-oblibene,body.in-favourites",
            blockHeadingOuterHtml: tb2.$h("h4", "Favourite Products"),
            emptyNoticeHtml: "Your list is unfortunately empty.",
            copyLinkBtnHtml: "Copy Link",
            copyLinkBtnCopiedHtml: "Copied",
            copyLinkLabelHtml: "Shareable Link",
            addBtnHtml: "Add to Favorites",
            removeBtnHtml: "Remove from Favorites"
          },
          de: {
            favouritePageSelector: "body.in-oblibene,body.in-favoriten",
            blockHeadingOuterHtml: tb2.$h("h4", "Bevorzugte Produkte"),
            emptyNoticeHtml: "Leider ist Ihre Liste leer.",
            copyLinkBtnHtml: "Link kopieren",
            copyLinkBtnCopiedHtml: "Kopiert",
            copyLinkLabelHtml: "Link zum Teilen",
            addBtnHtml: "Zu den Favoriten hinzuf\xFCgen",
            removeBtnHtml: "Aus den Favoriten l\xF6schen"
          }
        }
      };
      const callbackPresets = {
        compat: {
          addBtnToDetailCallback($detail, active) {
            var $div = $detail.find(".dkLabFavDiv").first();
            if (!$div.length) {
              $div = tb2.$h(".dkLabFavDiv.dklabFavStyl2");
              if ($detail.find(".product-top .add-to-cart, #choose-variant").length) {
                $detail.find(".product-top .add-to-cart, #choose-variant").after($div);
              } else {
                $detail.find(".p-info-wrapper .detail-parameters").after($div);
              }
            }
            if (active) {
              var $flags = $detail.find(".flags.flags-default").first();
              if (!$flags.length)
                $flags = $h2(".flags.flags-default.flags-inline").prependTo($detail.find(".p-detail-info"));
              $flags.append($h2("span.flag.flag-dklabFav", [$h2("span.heart")]));
              $div.html('<span class="dkLabFavouriteControls dkFavSmazat" id="dkLabRemoveFavourite"><strong class="heart"></strong><span>' + t.removeBtnHtml + "</span></span>");
            } else {
              $detail.find(".flag-dklabFav").remove();
              $div.html('<span class="dkLabFavouriteControls dkFavPridat" id="dkLabAddFavourite"><strong class="heart"></strong><span>' + t.addBtnHtml + "</span></span>");
            }
            return $div.find(".dkLabFavouriteControls");
          },
          addBtnToProductBlockCallback($p, active) {
            var $inactiveBtn = $('<div class="dkLabFavouriteDiv dklabFavStyl2"><span class="dkLabFavouriteControls dkFavPridat"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="25" height="24" class="heart"><defs><symbol id="heart"><path d="M12 21.35l-1.45-1.32c-5.15-4.67-8.55-7.75-8.55-11.53 0-3.08 2.42-5.5 5.5-5.5 1.74 0 3.41.81 4.5 2.09 1.09-1.28 2.76-2.09 4.5-2.09 3.08 0 5.5 2.42 5.5 5.5 0 3.78-3.4 6.86-8.55 11.54l-1.45 1.31z"/></symbol></defs><use xlink:href="#heart" x="0" y="0" /></svg><strong class="heart"></strong><span>' + t.addBtnHtml + "</span></span></div>"), $activeBtn = $('<div class="dkLabFavouriteDiv dklabFavStyl2"><span class="dkLabFavouriteControls dkFavSmazat"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="25" height="24" class="heart"><defs><symbol id="heart"><path d="M12 21.35l-1.45-1.32c-5.15-4.67-8.55-7.75-8.55-11.53 0-3.08 2.42-5.5 5.5-5.5 1.74 0 3.41.81 4.5 2.09 1.09-1.28 2.76-2.09 4.5-2.09 3.08 0 5.5 2.42 5.5 5.5 0 3.78-3.4 6.86-8.55 11.54l-1.45 1.31z"/></symbol></defs><use xlink:href="#heart" x="0" y="0" /></svg><strong class="heart"></strong><span>' + t.removeBtnHtml + "</span></span></div>");
            if (active) {
              var $flags = $p.find(".flags.flags-default:first");
              if (!$flags.length)
                $flags = $h2(".flags.flags-default").appendTo($p.find(".image"));
              $flags.append($h2("span.flag.flag-dklabFav", [$h2("span.heart")]));
              return $activeBtn.insertAfter($p.find(".name:first"));
            }
            $p.find(".flag-dklabFav").remove();
            return $inactiveBtn.insertAfter($p.find(".name:first"));
          }
        },
        clean: {
          addBtnToDetailCallback($detail, active) {
            var $div = $detail.find(".dkLabFavDiv").first();
            if (!$div.length) {
              $div = tb2.$h(".dkLabFavDiv.dklabFavStyl2");
              if ($detail.find(".product-top .add-to-cart, #choose-variant").length) {
                $detail.find(".product-top .add-to-cart, #choose-variant").after($div);
              } else {
                $detail.find(".p-info-wrapper .detail-parameters").after($div);
              }
            }
            if (active) {
              var $flags = $detail.find(".flags.flags-default").first();
              if (!$flags.length)
                $flags = $h2(".flags.flags-default.flags-inline").prependTo($detail.find(".p-detail-info"));
              $flags.append($h2("span.flag.flag-dklabFav", [$h2("span.heart")]));
              $div.html('<span class="dkLabFavouriteControls dkFavSmazat" id="dkLabRemoveFavourite"><strong class="heart"></strong><span>' + t.removeBtnHtml + "</span></span>");
            } else {
              $detail.find(".flag-dklabFav").remove();
              $div.html('<span class="dkLabFavouriteControls dkFavPridat" id="dkLabAddFavourite"><strong class="heart"></strong><span>' + t.addBtnHtml + "</span></span>");
            }
            return $div.find(".dkLabFavouriteControls");
          },
          addBtnToProductBlockCallback: (() => {
            const $inactiveBtn = $('<div class="dkLabFavouriteDiv dklabFavStyl2"><span class="dkLabFavouriteControls dkFavPridat"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="25" height="24" class="heart"><defs><symbol id="heart"><path d="M12 21.35l-1.45-1.32c-5.15-4.67-8.55-7.75-8.55-11.53 0-3.08 2.42-5.5 5.5-5.5 1.74 0 3.41.81 4.5 2.09 1.09-1.28 2.76-2.09 4.5-2.09 3.08 0 5.5 2.42 5.5 5.5 0 3.78-3.4 6.86-8.55 11.54l-1.45 1.31z"/></symbol></defs><use xlink:href="#heart" x="0" y="0" /></svg></span></div>');
            const $activeBtn = $('<div class="dkLabFavouriteDiv dklabFavStyl2"><span class="dkLabFavouriteControls dkFavSmazat"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="25" height="24" class="heart"><defs><symbol id="heart"><path d="M12 21.35l-1.45-1.32c-5.15-4.67-8.55-7.75-8.55-11.53 0-3.08 2.42-5.5 5.5-5.5 1.74 0 3.41.81 4.5 2.09 1.09-1.28 2.76-2.09 4.5-2.09 3.08 0 5.5 2.42 5.5 5.5 0 3.78-3.4 6.86-8.55 11.54l-1.45 1.31z"/></symbol></defs><use xlink:href="#heart" x="0" y="0" /></svg></span></div>');
            const $favFlag = $h2("span.flag.flag-dklabFav", [$h2("span.heart")]);
            return ($p, active) => {
              if (active) {
                var $flags = $p.find(".flags.flags-default:first");
                if (!$flags.length)
                  $flags = $h2(".flags.flags-default").appendTo($p.find(".image"));
                $flags.append($favFlag.clone());
                return $activeBtn.clone().insertAfter($p.find(".name:first"));
              }
              $p.find(".flag-dklabFav").remove();
              return $inactiveBtn.clone().insertAfter($p.find(".name:first"));
            };
          })()
        }
      };
      let t;
      function getStrings() {
        const dl = getShoptetDataLayer();
        const lang = dl ? dl.language : config2.fallbackLang;
        return config2.trans[lang] || config2.trans[config2.fallbackLang] || {};
      }
      function getStringCompat(key) {
        return config2[key] || t[key] || "";
      }
      function getProducts() {
        return JSON.parse(localStorage.getItem(config2.storageKey) || "[]");
      }
      function setProducts(products) {
        localStorage.setItem(config2.storageKey, JSON.stringify(products));
      }
      function isFavouriteProductFactory() {
        var products = getProducts(), productsByUrl = tb2.common.indexBy(products, function(p) {
          return p.url;
        }), productsByIdentifier = tb2.common.indexBy(products, function(p) {
          return p.identifier;
        }), productsById = tb2.common.indexBy(products, function(p) {
          return p.productId;
        });
        return function isFavouriteProduct(info) {
          if (info.url && productsByUrl.hasOwnProperty(info.url))
            return true;
          if (info.identifier && productsByIdentifier.hasOwnProperty(info.identifier))
            return true;
          if (info.productId && productsById.hasOwnProperty(info.productId))
            return true;
          return false;
        };
      }
      function getProductBlockInfo($p) {
        return {
          productId: $p.data("microProductId"),
          identifier: $p.data("microIdentifier"),
          url: $p.find("[data-micro=url]").attr("href")
        };
      }
      function updateProductBlocks(force) {
        var $productBlocks = $(config2.productBlockSelector);
        if (!$productBlocks.length)
          return false;
        var isFavouriteProduct = isFavouriteProductFactory(), changed = false;
        $productBlocks.each(function() {
          var $p = $(this);
          if (!tb2.dom.oneTime($p, "FavouriteProducts") && !force)
            return;
          var info = getProductBlockInfo($p);
          if (force)
            $p.find(".favourite-products-toggle").remove();
          var callback = config2.addBtnToProductBlockCallback || callbackPresets[config2.callbackPreset].addBtnToProductBlockCallback;
          var $btn = callback($p, isFavouriteProduct(info));
          $btn.addClass("favourite-products-toggle");
          changed = true;
        });
        return changed;
      }
      let lastDetailUrl = null;
      function updateDetail(force) {
        var $detail = $(".p-detail");
        if (!$detail.length)
          return false;
        let info = ProductInfo.getDetailProductInfo($detail);
        info = transformProductInfo(info);
        if (lastDetailUrl !== null && lastDetailUrl !== info.url)
          force = true;
        lastDetailUrl = info.url;
        if (!tb2.dom.oneTime($detail, "FavouriteProducts") && !force)
          return false;
        var isFavouriteProduct = isFavouriteProductFactory();
        if (force)
          $detail.find(".favourite-products-toggle.--detail").remove();
        var callback = config2.addBtnToDetailCallback || callbackPresets[config2.callbackPreset].addBtnToDetailCallback;
        var $btn = callback($detail, isFavouriteProduct(info));
        $btn.addClass("favourite-products-toggle --detail");
        return true;
      }
      function updateFavouriteBlocks() {
        var products = getProducts();
        var $blocks = $(".favourite-block:not(.--view-only)");
        $blocks.each(function() {
          var $newBlock = renderFavouriteBlockInner(products);
          $(this).replaceWith($newBlock);
        });
        if ($blocks.length)
          shoptet.images.unveil();
      }
      function updateFavouriteCount() {
        var products = getProducts();
        $(".favourite-count").text(products.length).toggleClass("--empty", !products.length);
      }
      function fetchProductInfo(url) {
        return tb2.ajax.loadDocument(url).then(function($doc) {
          var $detail = $doc.find(".p-detail");
          return $detail.length && ProductInfo.getDetailProductInfo($detail);
        });
      }
      const tmpLink = document.createElement("a");
      function canonicalizeUrl(url) {
        tmpLink.href = url;
        let pathname = tmpLink.pathname;
        if (pathname.slice(-1) !== "/")
          pathname += "/";
        return pathname;
      }
      function transformProductInfo(info) {
        if (info && info.url)
          info.url = canonicalizeUrl(info.url);
        return info;
      }
      function toggleFavouriteProduct(info, isDetailInfo, newActive) {
        info = transformProductInfo(info);
        const products = getProducts();
        const activeIndex = tb2.common.findIndex(products, function(p) {
          if (info.url && p.url === info.url)
            return true;
          if (info.identifier && p.identifier === info.identifier)
            return true;
          if (info.productId && p.productId === info.productId)
            return true;
          return false;
        });
        if (newActive === void 0)
          newActive = activeIndex === -1;
        if (activeIndex === -1 && !newActive || activeIndex !== -1 && newActive)
          return Promise.resolve(newActive);
        if (newActive && !isDetailInfo)
          return fetchProductInfo(info.url).then(function(newInfo) {
            return toggleFavouriteProduct(newInfo, true, newActive);
          });
        if (newActive)
          products.push(transformProductInfo(info));
        else
          products.splice(activeIndex, 1);
        setProducts(products);
        updateFavouriteBlocks();
        updateDetail(true);
        updateProductBlocks(true);
        updateFavouriteCount();
        tb2.hooks.signalDomLoad();
        return Promise.resolve(newActive);
      }
      function renderProductBlock(info) {
        const renderOptions = Object.assign({}, config2.renderOptions);
        if (!info.timestamp || Date.now() - info.timestamp > config2.maxPriceLifetime) {
          renderOptions.showPrice = renderOptions.showAvailability = false;
        }
        return ProductInfo.renderProductBlock(info, renderOptions).data("favouriteProductInfo", info);
      }
      function getShareUrl(products) {
        var hash = "#" + btoa(products.map(function(info) {
          return canonicalizeUrl(info.url);
        }).join(","));
        var shareUrl = new URL(location.href);
        shareUrl.hash = hash;
        return shareUrl.toString();
      }
      function fetchProductsFromHash(hash) {
        hash = hash.slice(1);
        if (hash === "")
          return Promise.resolve([]);
        return Promise.resolve().then(function() {
          var urls = atob(hash).split(",").map(function(url) {
            return canonicalizeUrl(url);
          }).filter(function(url) {
            return !!url;
          });
          return Promise.all(urls.map(function(url) {
            return fetchProductInfo(url);
          }));
        }).then(function(products) {
          return products.filter(function(info) {
            return !!info;
          });
        });
      }
      const updatingProducts = {};
      function updateStaleProduct(url, $p) {
        if (updatingProducts[url]) {
          updatingProducts[url].push($p);
          return;
        }
        updatingProducts[url] = [$p];
        let products = getProducts();
        let info = tb2.common.find(products, tb2.common.matcher("url", url));
        if (!info || info.timestamp && Date.now() - info.timestamp <= config2.maxPriceLifetime || info.lastUpdateAttempt && Date.now() - info.lastUpdateAttempt <= config2.maxPriceLifetime / 2) {
          delete updatingProducts[url];
          return;
        }
        console.log("Updating", info.url);
        return fetchProductInfo(info.url).catch(function(err) {
          console.error(err);
          return null;
        }).then(function(newInfo) {
          let products2 = getProducts();
          let index = tb2.common.findIndex(products2, tb2.common.matcher("url", url));
          if (index === -1) {
            delete updatingProducts[url];
            return;
          }
          if (!newInfo) {
            console.log("Update failed", info.url);
            products2[index].lastUpdateAttempt = Date.now();
            setProducts(products2);
          } else {
            products2[index] = transformProductInfo(newInfo);
            setProducts(products2);
            for (let $p2 of updatingProducts[url]) {
              if (!document.contains($p2[0]))
                continue;
              let $newP = renderProductBlock(newInfo);
              $p2.replaceWith($newP);
            }
            tb2.hooks.signalDomLoad();
            shoptet.images.unveil();
          }
          delete updatingProducts[url];
        });
      }
      function updateVisibleStaleProducts() {
        if (isViewingSharedFavourites || config2.maxPriceLifetime <= 0)
          return;
        var scrollTop = $(window).scrollTop(), windowHeight = $(window).height();
        $(".favourite-block .p").each(function() {
          var $t2 = $(this);
          var offsetTop = $t2.offset().top;
          if (offsetTop > scrollTop && offsetTop < scrollTop + windowHeight)
            updateStaleProduct($t2.data("favouriteProductInfo").url, $t2);
        });
      }
      function renderShareBtn(products) {
        var shareUrl = getShareUrl(products);
        return $h2(".favourite-products-share", [
          $h2("button.btn.btn-primary", { type: "button" }, [getStringCompat("copyLinkBtnHtml")]),
          $h2(".favourite-products-share__tooltip", [
            $h2("label", { for: "favouriteProductsShareUrl" }, [getStringCompat("copyLinkLabelHtml")]),
            $h2("input", { id: "favouriteProductsShareUrl", readonly: true, value: shareUrl })
          ])
        ]);
      }
      function renderFavouriteBlockInner(products, renderConfig = {}) {
        renderConfig = Object.assign({}, config2, renderConfig);
        products = products.map(function(info) {
          return $h2("div", { class: renderConfig.itemClass }, [
            renderProductBlock(info)
          ]);
        });
        return $h2("div", { class: renderConfig.listClass }, products).addClass("favourite-block");
      }
      function renderFavouriteBlock(renderConfig = {}) {
        var products = getProducts();
        if (!products.length)
          return $();
        renderConfig = Object.assign({}, config2, renderConfig);
        return $h2("div", { class: renderConfig.wrapperClass }, [
          tb2.$h("div", [renderConfig.heading || t.blockHeadingOuterHtml || ""]).clone().contents(),
          renderFavouriteBlockInner(products, renderConfig)
        ]);
      }
      var $shareBtn, $productBlock, $emptyNotice, isViewingSharedFavourites = false;
      function initFavouritePage() {
        fetchProductsFromHash(location.hash).catch(function(err) {
          console.error(err);
          return [];
        }).then(function(products) {
          isViewingSharedFavourites = true;
          if (!products.length) {
            products = getProducts();
            isViewingSharedFavourites = false;
          }
          if ($shareBtn)
            $shareBtn.remove();
          if ($productBlock)
            $productBlock.remove();
          if ($emptyNotice)
            $emptyNotice.remove();
          var $article = $(".content-inner > article");
          if (!products.length) {
            $emptyNotice = $h2(".favourite-products-empty", [getStringCompat("emptyNoticeHtml")]).insertAfter($article);
            return;
          }
          if (!isViewingSharedFavourites)
            $shareBtn = renderShareBtn(products).insertBefore($article);
          $productBlock = renderFavouriteBlockInner(products).insertAfter($article);
          if (isViewingSharedFavourites)
            $productBlock.addClass("--view-only");
          else
            updateVisibleStaleProducts();
          tb2.hooks.signalDomLoad();
          shoptet.images.unveil();
        });
      }
      function isFavouritePage() {
        return !!$(getStringCompat("favouritePageSelector")).length;
      }
      let copiedTimeout;
      const init = tb2.dom.createModuleInitializer({
        earlyInit() {
          t = getStrings();
        },
        domReady: function() {
          $("body").on("click", ".favourite-products-toggle", function(e) {
            e.preventDefault();
            const $p = $(this).closest(".p,.p-detail");
            if (!$p.length)
              return;
            const isDetail = $p.is(".p-detail");
            const info = isDetail ? ProductInfo.getDetailProductInfo($p) : getProductBlockInfo($p);
            toggleFavouriteProduct(info, isDetail);
          });
          if (isFavouritePage()) {
            initFavouritePage();
            $(window).on("hashchange", initFavouritePage);
            $(window).on("scroll", updateVisibleStaleProducts);
          }
          $("body").on("click", ".favourite-products-share button", function(e) {
            e.preventDefault();
            const $btn = $(this);
            $("#favouriteProductsShareUrl").select();
            document.execCommand("copy");
            $btn.html(t.copyLinkBtnCopiedHtml);
            clearTimeout(copiedTimeout);
            copiedTimeout = setTimeout(function() {
              $btn.html(getStringCompat("copyLinkBtnHtml"));
            }, 2500);
          });
        },
        render: function() {
          var changed = false;
          changed |= !!updateProductBlocks();
          changed |= !!updateDetail();
          updateFavouriteCount();
          if (changed)
            tb2.hooks.signalDomLoad();
        }
      });
      return {
        init,
        config: config2,
        callbackPresets,
        isFavouriteProductFactory,
        toggleFavouriteProduct,
        getProducts,
        renderProductBlock,
        renderFavouriteBlock,
        renderFavouriteBlockInner,
        updateVisibleStaleProducts,
        isFavouritePage
      };
    }(tb2.$h);
    const GoogleMaps = function($h2) {
      const config2 = {
        autoload: true,
        apiKey: "",
        libraries: ""
        // geometry
      };
      let ready$resolve;
      const ready = new Promise((resolve) => ready$resolve = resolve);
      let loading = false;
      function load() {
        if (!loading) {
          loading = true;
          let url = "https://maps.googleapis.com/maps/api/js?";
          const params = [];
          params.push("key=" + config2.apiKey);
          params.push("libraries=" + config2.libraries);
          params.push("callback=initMap");
          url += params.join("&");
          window.initMap = window.initMap || (() => {
          });
          tb2.hooks.hookFn("window.initMap", (initMap, args, context) => {
            initMap.apply(context, args);
            ready$resolve();
          });
          $(() => {
            $("head").append($h2("script", { src: url, async: true }));
          });
        }
        return ready;
      }
      const init = tb2.dom.createModuleInitializer({
        earlyInit() {
          if (config2.autoload)
            load();
        }
      });
      return { config: config2, init, load, ready };
    }(tb2.$h);
    const LeftToFreeShippingFix = function(AdvancedOrderHooks2) {
      function updateDataLayer(content) {
        const $content = tb2.$h("div", [content]), $delivery = $content.find(".extra.delivery > span");
        const cartInfo = getShoptetDataLayer("cartInfo");
        if (!cartInfo)
          return;
        const $priceLeft = $delivery.children("strong").first();
        if (!$priceLeft.length)
          return;
        const priceLeft = tb2.common.parsePrice($priceLeft.text());
        if (isNaN(priceLeft))
          cartInfo.freeShipping = true;
        else {
          cartInfo.freeShipping = false;
          cartInfo.leftToFreeShipping.priceLeft = priceLeft;
          cartInfo.leftToFreeShipping.formattedPrice = priceLeft.ShoptetFormatAsCurrency();
        }
      }
      const init = tb2.dom.createModuleInitializer({
        earlyInit() {
          AdvancedOrderHooks2.init(true);
          tb2.hooks.hookFn("shoptet.tracking.updateDataLayerCartInfo", function(updateDataLayerCartInfo, args, context) {
            const response = args[0];
            const getNoBillingShippingPrice = response.getFromPayload("getNoBillingShippingPrice");
            const cartInfo = getShoptetDataLayer("cartInfo");
            if (cartInfo && getNoBillingShippingPrice)
              cartInfo.getNoBillingShippingPrice = getNoBillingShippingPrice;
            return updateDataLayerCartInfo.apply(context, args);
          });
          AdvancedOrderHooks2.addContentFilter(function(content) {
            updateDataLayer(content);
            return content;
          });
        }
      });
      return { init };
    }(AdvancedOrderHooks);
    const MessageDismissFix = function() {
      const config2 = {
        manualDismissErrors: false
      };
      function appendDismissButtons() {
        $(".msg.msg-error").each(function() {
          if (tb2.dom.oneTime(this, "MessageDismissFix"))
            $(this).append(tb2.$h("button.msg__dismiss-btn", ["&times;"])).addClass("msg-dismissible");
        });
      }
      const init = tb2.dom.createModuleInitializer({
        earlyInit() {
          tb2.hooks.hookFn("window.showMessage", function(showMessage, args, context) {
            clearTimeout(shoptet.runtime.dismiss);
            showMessage.apply(context, args);
            const type = args[1];
            if (config2.manualDismissErrors && type === "error") {
              clearTimeout(shoptet.runtime.dismiss);
              appendDismissButtons();
            }
          });
          $("body").on("click", ".msg__dismiss-btn", function(e) {
            e.preventDefault();
            hideMsg(true);
          });
          $("body").on("click", function(e) {
            if ($(".msg.msg-error").length && !$(e.target).closest(".msg").length)
              hideMsg(true);
          });
          $("html").on("click", ".msg.msg-dismissible", function(e) {
            e.stopImmediatePropagation();
          });
        }
      });
      return { config: config2, init };
    }();
    const ProductInfo = function($h2, $t2) {
      const config2 = {
        defaultRenderOptions: {
          alwaysShowDetailBtn: false,
          showDescription: false,
          maxDescriptionLength: -1,
          showSku: true,
          showPrices: true,
          showBtn: true,
          clonePriceSaveIntoBottom: true,
          showAvailability: true,
          showPriceAdditional: true,
          pricesVersion: 1
        },
        fallbackLang: "cs",
        trans: {
          cs: {
            code: "K\xF3d: "
          }
        }
      };
      function getDetailProductInfo($detail) {
        init();
        const $form = $detail.is("#product-detail-form") ? $detail : $detail.find("#product-detail-form");
        function excludeProductBlocks() {
          return !$(this).closest(".p").length;
        }
        function getHiddenInput(name) {
          return $form.find('input[name="' + name + '"]').filter(excludeProductBlocks).first().val();
        }
        function getMeta(prop) {
          const $el = $detail.find('[itemprop="' + prop + '"]').filter(excludeProductBlocks).first();
          if ($el.is("meta"))
            return $el.attr("content");
          if ($el.is("link"))
            return $el.attr("href");
          return $el.text().trim();
        }
        const info = {};
        info.productId = getHiddenInput("productId");
        info.priceId = getHiddenInput("priceId");
        info.language = getHiddenInput("language");
        const metaKeys = [
          "name",
          "identifier",
          "url",
          "price",
          "priceCurrency",
          "availability",
          "description",
          "url",
          "warranty",
          "sku",
          "image",
          "ratingValue",
          "ratingCount"
        ];
        for (let key of metaKeys)
          info[key] = getMeta(key);
        const $productAppendix = $detail.find("h1 .product-appendix");
        if ($productAppendix.length) {
          info.name = tb2.dom.getTextShallow($productAppendix.parent()).trim();
          info.appendix = $productAppendix.text().trim();
        }
        info.descriptionHtml = $detail.find(".p-short-description").html()?.trim() || "";
        info.url = new URL(info.url, location.href).pathname;
        info.image = info.image ? tb2.sh.convertImageUrl(info.image, "detail") : null;
        function collectFlags(selector) {
          return ($detail.find(selector).filter(excludeProductBlocks).filter(function() {
            return $(this).html().trim() !== "";
          }).first().html() ?? "").trim();
        }
        info.flagsDefault = collectFlags(".flags.flags-default");
        info.flagsExtra = collectFlags(".flags.flags-extra");
        info.ratingCount = info.ratingCount ? tb2.common.parseNumber(info.ratingCount) : 0;
        info.ratingValue = info.ratingValue ? tb2.common.parseNumber(info.ratingValue) : 0;
        info.showDetailBtn = false;
        let $availabilityValue = $detail.find(".availability-value").filter(excludeProductBlocks).first();
        if ($availabilityValue.find(".choose-variant,.parameter-dependent").length) {
          info.showDetailBtn = true;
          $availabilityValue = $availabilityValue.find(".choose-variant,.parameter-dependent").first();
        }
        info.availabilityHtml = $availabilityValue.html();
        info.priceHtml = $detail.find(".p-final-price-wrapper").filter(excludeProductBlocks).html();
        info.timestamp = Date.now();
        return info;
      }
      function migrateFlags(info) {
        const $flags = tb2.$h("div", [info.flags]);
        const $flagsExtra = tb2.$h("div", [$flags.find(".flag.flag-discount")]);
        info.flagsDefault = $flags.html().trim();
        const $prices = $h2(".prices", [info.priceHtml]);
        if ($prices.find(".price-save").length && !$flagsExtra.find(".flag.flag-discount").length)
          $flagsExtra.append(tb2.$h("span.flag.flag-discount", [
            $prices.find(".price-standard"),
            $prices.find(".price-save")
          ]));
        info.flagsExtra = $flagsExtra.html().trim();
      }
      function renderProductBlock(info, options) {
        init();
        options = Object.assign({}, config2.defaultRenderOptions, options);
        const $availability = $h2(".availability", [info.availabilityHtml]);
        if (!options.showAvailability)
          $availability.empty();
        const $avLabel = $availability.find(".availability-label").removeClass("availability-label");
        $avLabel.attr("style", ($avLabel.attr("style") || "").replace(/color:\s+/, "color:"));
        const $avTooltip = $avLabel.find(".show-tooltip");
        if ($avTooltip.length)
          $avLabel.addClass("show-tooltip acronym").attr("title", $avTooltip.attr("title")).html($avTooltip.contents());
        let $starsWrapper;
        if (info.ratingCount > 0) {
          const $stars = $h2("span.stars.stars-list");
          for (let i = 1; i <= 5; i++)
            $stars.append($h2("span.star").toggleClass("star-on", info.ratingValue >= i).toggleClass("star-off", info.ratingValue < i));
          $starsWrapper = $h2(".stars-wrapper", {
            "data-micro-rating-value": info.ratingValue,
            "data-micro-rating-count": info.ratingCount
          }, $stars);
        } else
          $starsWrapper = $h2(".stars-placeholder");
        const $prices = $h2(".prices", [info.priceHtml]);
        if (options.pricesVersion >= 2) {
          let $priceFinalHolder = $prices.find(".price-final-holder.default-variant");
          if (!$priceFinalHolder.length)
            $priceFinalHolder = $prices.find(".price-final-holder");
          let $priceAdditional = $prices.find(".price-additional .default-variant");
          if (!$priceAdditional.length)
            $priceAdditional = $prices.find(".price-additional");
          if ($priceAdditional.length) {
            if ($priceAdditional.find(".nowrap").length)
              $priceAdditional.html((_, html) => html.replace(/<span class="nowrap">([^<]+)<\/span>\s*/, "$1&nbsp;").trim());
            else
              $priceAdditional.html((_, html) => html.replace(/\s+(bez DPH)/, "&nbsp;$1").trim());
          }
          $priceFinalHolder.html((_, html) => html.replace(/([^<]+)<span class="nowrap">([^<]+)<\/span>/, ($0, $1, $2) => `<small>${$1.trim()}</small> ${$2}`));
          $prices.html([
            options.showPriceAdditional && $priceAdditional.length ? $h2(".price-additional", $priceAdditional.contents()) : null,
            $h2(".price.price-final", { "data-testid": "productCardPrice" }, [
              $h2("strong", $priceFinalHolder.contents())
            ])
          ]);
        } else {
          $prices.find(".price-measure").remove();
          $prices.find(".detail-prices").unwrap();
          $prices.find(".price-final").addClass("price");
        }
        if (!options.clonePriceSaveIntoBottom) {
          $prices.find(".price-standard,.price-save").remove();
        }
        if (!options.showPrices)
          $prices.empty();
        function createHiddenInput(name, value) {
          return $h2("input", { type: "hidden", name, value });
        }
        const $tools = $h2(".p-tools");
        if (options.showBtn) {
          if (info.showDetailBtn || options.alwaysShowDetailBtn)
            $tools.append($h2("a.btn.btn-primary", { href: info.url }, "Detail"));
          else
            $tools.append($h2("form.pr-action", {
              action: "/action/Cart/addCartItem/",
              method: "post"
            }, [
              createHiddenInput("language", info.language),
              createHiddenInput("priceId", info.priceId),
              createHiddenInput("productId", info.productId),
              $h2("button.btn.btn-cart.add-to-cart-button", {
                type: "submit"
              }, [
                $h2("span", "Do ko\u0161\xEDku")
              ])
            ]));
        }
        function truncate(str, maxLength, append) {
          str = str.trim();
          if (str.length < maxLength)
            return str;
          const parts = str.split(/([ ,.:;"'_-])/);
          parts.push("");
          let res = "", lastDelimiter = "";
          for (let i = 0; i < parts.length; i += 2) {
            let nextWord = "";
            for (; i < parts.length; i += 2) {
              nextWord += parts[i];
              if (parts[i] !== "")
                break;
              else
                nextWord += parts[i + 1];
            }
            if (res.length + lastDelimiter.length + nextWord.length + append.length > maxLength)
              break;
            res += lastDelimiter + nextWord;
            lastDelimiter = parts[i + 1];
          }
          return res.length ? res + append : "";
        }
        let description = $h2("div", [info.description]).text();
        if (options.maxDescriptionLength > -1)
          description = truncate(description, options.maxDescriptionLength, "...");
        else if (info.descriptionHtml)
          description = [info.descriptionHtml];
        if (info.flags && !info.flagsDefault && !info.flagsExtra)
          migrateFlags(info);
        const $flagsDefault = info.flagsDefault ? $h2(".flags.flags-default", [info.flagsDefault]) : null;
        const $flagsExtra = info.flagsExtra ? $h2(".flags.flags-extra", [info.flagsExtra]) : null;
        if ($flagsExtra) {
          const $priceStandard = $flagsExtra.find(".price-standard");
          if ($priceStandard.length) {
            const $v = $priceStandard.find(".parameter-dependent.default-variant");
            let newHtml = $v.length ? $v.html().trim() : $priceStandard.html().trim();
            let m;
            if (m = newHtml.match(/^([\p{L}\s]+)(.*)$/u)) {
              newHtml = m[2];
              $priceStandard.before(" " + m[1]);
            }
            $priceStandard.html(newHtml);
          }
          const $priceSave = $flagsExtra.find(".price-save");
          if ($priceSave.length) {
            const $v = $priceSave.find(".parameter-dependent.default-variant");
            let newHtml = $v.length ? $v.html().trim() : $priceSave.html().trim();
            let m;
            if (m = newHtml.match(/^([\p{L}\s]+)(.*)$/u)) {
              newHtml = m[2];
              $priceSave.before(" " + m[1]);
            }
            $priceSave.html(newHtml);
          }
        }
        return $h2(".p", {
          "data-micro": "product",
          "data-micro-product-id": info.productId,
          "data-micro-identifier": info.identifier
        }, [
          $h2("a.image", { href: info.url }, [
            $h2("img", {
              src: "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
              "data-src": info.image,
              alt: info.name,
              "data-micro-image": info.image
            }),
            $flagsDefault,
            $flagsExtra
          ]),
          $h2(".p-in", [
            $h2(".p-in-in", [
              $h2("a.name", {
                href: info.url,
                "data-micro": "url"
              }, [
                $h2("span", { "data-micro": "name" }, [
                  $t2(info.name),
                  ...info.appendix ? [
                    " ",
                    // space between name and appendix when selecting the whole title
                    $h2("span.category-appendix", info.appendix)
                  ] : []
                ])
              ]),
              $h2(".ratings-wrapper", [$starsWrapper, $availability])
            ]),
            $h2(".p-bottom", [
              $h2("div", {
                "data-micro": "offer",
                "data-micro-price": info.price,
                "data-micro-price-currency": info.priceCurrency,
                "data-micro-warranty": info.warranty,
                "data-micro-availability": info.availability
              }, [
                $prices,
                $tools,
                options.showDescription ? $h2(".p-desc", { "data-micro": "description" }, description) : null
              ])
            ])
          ]),
          options.showSku ? $h2(".p-code", [
            t.code,
            $h2("span", { "data-micro": "sku" }, info.sku)
          ]) : $h2("span.no-display", { "data-micro": "sku" }, info.sku)
        ]);
      }
      let t;
      const init = tb2.dom.createModuleInitializer({
        earlyInit() {
          t = tb2.helpers.getStrings(config2);
        }
      });
      return { init, config: config2, getDetailProductInfo, renderProductBlock };
    }(tb2.$h, tb2.$t);
    const RecentProductsLib = function($h2, ProductInfo2) {
      const config2 = {
        storageKey: "recentProducts",
        newestFirst: false,
        alwaysShowDetailBtn: false,
        limit: -1,
        maxLifetime: -1,
        maxDescriptionLength: -1,
        wrapperClass: "products-recent-wrapper",
        heading: $h2("h2.products-recent-header", "Naposledy jste prohl\xED\u017Eeli"),
        listClass: "products products-block products-recent products-additional p-switchable",
        itemClass: "product col-sm-6 col-md-12 col-lg-6 active related-sm-screen-show"
      };
      function getProducts() {
        return JSON.parse(localStorage.getItem(config2.storageKey) || "[]");
      }
      function setProducts(products) {
        localStorage.setItem(config2.storageKey, JSON.stringify(products));
      }
      function truncateList(list) {
        if (config2.maxLifetime > -1) {
          var now = Date.now();
          list = list.filter(function(info) {
            return info.timestamp && now - info.timestamp <= config2.maxLifetime;
          });
        }
        if (config2.limit > -1)
          list = list.slice(-config2.limit);
        return list;
      }
      function storeCurrentProduct() {
        var info = ProductInfo2.getDetailProductInfo($(".p-detail"));
        var list = getProducts();
        list = list.filter(function(p) {
          return p.productId !== info.productId || p.priceId !== info.priceId;
        });
        list.push(info);
        list = truncateList(list);
        setProducts(list);
      }
      function renderRecentlyViewedBlock() {
        var list = getProducts();
        list = truncateList(list);
        if (!list.length)
          return $();
        if (config2.newestFirst)
          list = list.reverse();
        var products = list.map(function(info) {
          return $h2("div", { class: config2.itemClass }, [
            ProductInfo2.renderProductBlock(info, config2)
          ]);
        });
        return $h2("div", { class: config2.wrapperClass }, [
          tb2.common.resolveValue(config2.heading),
          $h2("div", { class: config2.listClass }, products)
        ]);
      }
      var init = tb2.dom.createModuleInitializer({
        earlyInit() {
          if ($("body.type-detail").length)
            storeCurrentProduct();
        }
      });
      return {
        init,
        config: config2,
        renderRecentlyViewedBlock
      };
    }(tb2.$h, ProductInfo);
    const ReplaceResponsiveNavBtn = function() {
      const config2 = {
        newHtml: ""
      };
      const init = tb2.dom.createModuleInitializer({
        earlyInit() {
          if (!$.fn.shpResponsiveNavigation)
            return;
          tb2.hooks.hookFn("$.fn.shpResponsiveNavigation", function(shpResponsiveNavigation, args, context) {
            shpResponsiveNavigation.apply(context, args);
            function updateNavBtn() {
              context.find(".navigation-btn").html(config2.newHtml);
            }
            $(window).resize(updateNavBtn);
            updateNavBtn();
            return context;
          });
        }
      });
      return { config: config2, init };
    }();
    const ResponsiveSubmenuHoverFix = function() {
      const init = tb2.dom.whenReady(function init2() {
        $("body").on("mouseover", "#navigation .ext", function(e) {
          if (!detectResolution(shoptet.abilities.config.navigation_breakpoint)) {
            e.stopPropagation();
          }
        });
      });
      return { init };
    }();
    const SeoBanners = function() {
      const config2 = {
        idMap: {},
        indexMap: {},
        clone: true,
        // clone on load
        remove: false
        // remove from DOM after initial load
      };
      let banners;
      function loadFromDom() {
        var $footer = $("#footer .custom-footer");
        if (banners || !$footer.length)
          return;
        let $toDelete = $();
        banners = {};
        for (let key in config2.idMap) {
          const id = config2.idMap[key];
          const $banner2 = $footer.find('[data-ec-promo-id="' + id + '"]').closest(".banner");
          banners[key] = config2.clone ? $banner2.clone() : $banner2;
          $toDelete = $toDelete.add($banner2);
        }
        for (let key in config2.indexMap) {
          var index = config2.indexMap[key], $banner = $footer.children().eq(index);
          banners[key] = config2.clone ? $banner.clone() : $banner;
          $toDelete = $toDelete.add($banner);
        }
        if (config2.remove)
          $toDelete.detach();
      }
      function get(key, clone = true) {
        loadFromDom();
        if (key) {
          const $banner = banners?.[key] || $();
          return clone ? $banner.clone() : $banner;
        }
        return banners;
      }
      const init = tb2.dom.createModuleInitializer({
        render() {
          if (config2.remove)
            loadFromDom();
        }
      });
      return { config: config2, init, get };
    }();
    const SlickUnveilFix = function() {
      const config2 = {};
      const init = tb2.dom.createModuleInitializer({
        earlyInit() {
          $("html").on("init reInit", ".slick-slider", () => {
            shoptet.images.unveil();
          });
        }
      });
      return { config: config2, init };
    }();
    const modules = {
      AdvancedOrderHooks,
      AjaxSubmitFormHooks,
      CarrierRemark,
      CartItemData,
      DelayedCart,
      DetailHooks,
      ExternalRenderEvents,
      FavouriteProducts,
      GoogleMaps,
      LeftToFreeShippingFix,
      MessageDismissFix,
      ProductInfo,
      RecentProductsLib,
      ReplaceResponsiveNavBtn,
      ResponsiveSubmenuHoverFix,
      SeoBanners,
      SlickUnveilFix
    };
    tb2.modules = tb2.m = modules;
  })(window.toolbox);
})();
